import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import theme from '../../styles/theme';
import { useVideoPlayer } from '../../hooks/useVideoPlayer';
import { useAnnotations } from '../../hooks/useAnnotations';
import { useVideoMode } from '../../hooks/useVideoMode';
import { useRallies } from '../../hooks/useRallies';
import { convertToNativeCoordinates, parseJSONAsync } from '../../utils/annotationUtils';
import { snapToFrame, getFrameTolerance, normalizeTimeToFrame } from '../../utils/timeUtils';
import { VIDEO_WIDTH, VIDEO_HEIGHT, KEYBOARD_SHORTCUTS, getFPS, setFPS, BALL_NOT_IN_FRAME } from '../../utils/constants';
import { getFrameForTime, getTimeForFrame, isFrameIndexReady } from '../../utils/frameIndex';
import { parseVideoString, parseFPS, isVideoFormat } from '../../utils/videoUtils';
import { getVideoMetadata } from '../../utils/videoMetadata';
import {
  encodeVideoToURL,
  clearVideoFromURL,
  generateShareableURL
} from '../../utils/urlParams';
import VideoControls from './VideoControls';
import Timeline from './Timeline';
import AnnotationLayer from './AnnotationLayer';
import CourtOverlay from './CourtOverlay';
import TrajectoryOverlay from './TrajectoryOverlay';
import Sidebar from '../layout/Sidebar';
import ShortcutsOverlay from '../ui/ShortcutsOverlay';
import TopViewPlotOverlay from '../ui/TopViewPlotOverlay';
import TutorialOverlay from '../ui/TutorialOverlay';
import { VideoIcon, EmptyStatePlayIcon } from '../ui/Icons';
import { keypointPredictionService } from '../../services/keypointPrediction';
import { cameraPredictionService } from '../../services/cameraPrediction';
import { buildScoringTree, flattenPoints, getActiveStartTime, getOutcome, advanceScore, advanceScoreExtended, computeGameWinner, computeNewNextGameScore, computeSetWinner, opp } from '../../utils/scoringTree';
import unitConversionService from '../../services/pixelToMeters';
import { playerDetectionService } from '../../services/playerDetection';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../ui/Button';
import FeedbackPopup from '../auth/FeedbackPopup';
import { useTutorial } from '../../hooks/useTutorial';
import AzureStoragePopup from '../azure/AzureStoragePopup';
import BillingModal from '../billing/BillingModal';
import PlansModal from '../billing/PlansModal';
import SendVideoPopup from '../ui/SendVideoPopup';
import { usageTrackingService } from '../../services/usageTracking';
import { videoUploadService } from '../../services/videoUploadService';

const VideoPlayerContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: flex-start;
  height: 100vh;
  background: ${theme.colors.background};
  font-family: ${theme.typography.fontFamily};
  padding: ${theme.spacing.xl} ${theme.spacing.xl} 0 ${theme.spacing.xl};
  box-sizing: border-box;
  gap: ${theme.spacing.xxxxl};
  overflow: hidden;
  width: 100vw;
  position: relative;
`;

const VideoArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 1;
  min-height: 0;
  flex-grow: 1;
  overflow: visible;
  justify-content: flex-start;
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: 100%;
  gap: ${theme.spacing.md};
  overflow: hidden;
`;

const VideoWrapper = styled.div`
  position: relative;
  width: ${VIDEO_WIDTH}px;
  height: ${VIDEO_HEIGHT}px;
  
  /* Responsive dimensions for smaller screens */
  @media screen and (max-width: 1440px) {
    width: 960px;
    height: 540px; /* Maintain 16:9 aspect ratio: 960 * (9/16) = 540 */
  }
  border: 1px solid ${theme.colors.border};
  cursor: ${props => props.isPaused ? 'crosshair' : 'default'};
  user-select: none;
  overflow: hidden;
  flex-shrink: 0;
  transform-origin: top left;
`;

const VideoContent = styled.div`
  width: ${VIDEO_WIDTH}px;
  height: ${VIDEO_HEIGHT}px;
  position: absolute;
  top: 0;
  left: 0;
  transform: ${props => `translate(${-props.pan.x}px, ${-props.pan.y}px) scale(${props.zoom})`};
  transform-origin: top left;
  pointer-events: none;
  
  /* Responsive dimensions for smaller screens */
  @media screen and (max-width: 1440px) {
    width: 960px;
    height: 540px; /* Maintain 16:9 aspect ratio: 960 * (9/16) = 540 */
  }
`;

const VideoCanvas = styled.canvas`
  width: 100%;
  height: 100%;
  pointer-events: none;
  cursor: crosshair;
`;

// Mirrors the line-call row of splitstep-app's BallInfoOverlay: a coloured IN/OUT chip
// with the confidence as plain text beside it, both on a translucent card.
const CallCard = styled.div`
  position: absolute;
  top: ${theme.spacing.sm};
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  padding: 0.3rem 0.55rem;
  background: rgba(8, 20, 48, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 0.35rem;
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.45);
  font-family: ${theme.typography.fontFamily};
  pointer-events: none;
  z-index: 6;
`;

// min-width reserves room for the wider of the two labels ("OUT") so the card doesn't
// resize, and the playhead doesn't appear to shift, as the call is toggled.
const InOutBadge = styled.span`
  box-sizing: border-box;
  display: inline-block;
  min-width: 2.9rem;
  text-align: center;
  font-size: 0.95rem;
  font-weight: 800;
  padding: 0.05rem 0.35rem;
  border-radius: 3px;
  color: #fff;
  background: ${props => props.$in ? '#16a34a' : '#dc2626'};
`;

// Sized for a three-digit reading; tabular figures keep the digits from jittering.
const CallConfidence = styled.span`
  box-sizing: border-box;
  display: inline-block;
  min-width: 2.9rem;
  text-align: right;
  font-size: 1.1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #fff;
`;



const EmptyState = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: ${theme.colors.textMuted};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.xxxxl};
  font-weight: ${theme.typography.fontWeights.medium};
  letter-spacing: ${theme.typography.letterSpacing.normal};
  text-align: center;
  background: #23242b;
`;

const EmptyStateIcon = styled.div`
  position: relative;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const EmptyStateVideoIcon = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 64px;
    height: 64px;
  }
`;

const EmptyStatePlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
`;

const EmptyStateLogo = styled.img`
  height: 240px;
  object-fit: contain;
  filter: ${theme.shadows.dropShadow};
  margin-bottom: ${theme.spacing.xl};
`;

const LogoContainer = styled.div`
  position: fixed;
  left: ${theme.spacing.xl};
  bottom: calc(${theme.spacing.xl} + ${theme.spacing.sm});
  width: 400px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  z-index: 9999;
  
  @media screen and (max-width: 1440px) {
    left: ${theme.spacing.xl};
    bottom: calc(${theme.spacing.md} + ${theme.spacing.xs});
  }
`;

const Logo = styled.img`
  height: 40px;
  object-fit: contain;
  filter: ${theme.shadows.dropShadow};
  pointer-events: none;
  
  @media screen and (max-width: 1440px) {
    height: 32px;
  }
`;

const FeedbackButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background-color: ${theme.colors.surface};
  color: ${theme.colors.text};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.md};
  font-family: ${theme.typography.fontFamily};
  font-size: ${theme.typography.fontSizes.sm};
  font-weight: ${theme.typography.fontWeights.medium};
  cursor: pointer;
  transition: ${theme.transitions.normal};
  
  &:hover {
    background-color: ${theme.colors.surfaceHover};
    border-color: ${theme.colors.primaryHover};
    box-shadow: ${theme.shadows.md};
  }
  
  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
  
  @media screen and (max-width: 1440px) {
    padding: ${theme.spacing.xs} ${theme.spacing.sm};
    font-size: ${theme.typography.fontSizes.xs};
    margin-left: ${theme.spacing.md};
  }
`;



const VideoPlayer = () => {
  const { logout, subscription, reloadSubscription, user, userProfile, isEmployee } = useAuth();

  // Name the saved strokes file after whoever made the calls
  const annotatorName = useMemo(
    () => userProfile?.displayName || user?.email?.split('@')[0] || '',
    [userProfile, user]
  );

  // Tutorial state
  const {
    isActive: isTutorialActive,
    currentStepIndex,
    totalSteps,
    currentStep,
    startTutorial,
    stopTutorial,
    nextStep,
    previousStep,
    skipTutorial,
    handleActionComplete,
  } = useTutorial();

  // Initialize video state (no auto-loading from URL)
  const [videoUrl, setVideoUrl] = useState('');

  // Store processed video URL for the video player
  const [processedVideoUrl, setProcessedVideoUrl] = useState('');
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [timelineZoom, setTimelineZoom] = useState(1);
  const [loadedFileName, setLoadedFileName] = useState('');
  const [annotationFileName, setAnnotationFileName] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [videoError, setVideoError] = useState('');
  const [localVideoFile, setLocalVideoFile] = useState(null);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [isAzurePopupOpen, setIsAzurePopupOpen] = useState(false);
  const [isBillingOpen, setIsBillingOpen] = useState(false);
  const [isPlansOpen, setIsPlansOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isSendVideoPopupOpen, setIsSendVideoPopupOpen] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Track mouse position for bounding box creation (using same logic as ball annotations)
  const [mousePosition, setMousePosition] = useState({ x: null, y: null });
  const videoWrapperRef = useRef(null);

  // State for API processing
  const [hasProcessedVideo, setHasProcessedVideo] = useState(false);
  const [isProcessingCamera, setIsProcessingCamera] = useState(false);
  const processingInProgressRef = useRef(false);

  // State for AI Assist checkbox (enables player detection & court calibration)
  const [saveCourtCoordinates, setSaveCourtCoordinates] = useState(false);
  const [storedCameraParams, setStoredCameraParams] = useState(null);
  const [showTrajectories, setShowTrajectories] = useState(false);
  const [showCall, setShowCall] = useState(false);

  // State for initial video dimensions when video is first loaded
  const [initialVideoDimensions, setInitialVideoDimensions] = useState(null);

  // State for tracking conversion debug info to detect non-deterministic behavior
  const [lastConversionDebug, setLastConversionDebug] = useState(null);

  // State for current display dimensions (responsive)
  const [displayDimensions, setDisplayDimensions] = useState({
    width: VIDEO_WIDTH,
    height: VIDEO_HEIGHT
  });

  // Custom hooks
  const {
    canvasRef,
    playerRef,
    isPaused,
    currentTime,
    overlayTime,
    videoDuration,
    handlePlayPauseClick,
    isPlayerReady,
    playbackRate,
    setPlaybackRate,
  } = useVideoPlayer(processedVideoUrl, localVideoFile);

  // Handler for AI Assist checkbox change
  const handleSaveCourtCoordinatesChange = (event) => {
    const isChecked = event.target.checked;
    setSaveCourtCoordinates(isChecked);

    // If checkbox is checked and video is already loaded, trigger action A
    if (isChecked && videoUrl && playerRef.current) {
      // Only process if we don't already have camera parameters
      if (!storedCameraParams) {
        processVideoWithAPIs(false); // Background processing
      }
    }
  };

  // Function to capture initial video dimensions when video is first loaded
  const captureInitialVideoDimensions = () => {
    const vw = playerRef.current?.videoWidth;
    const vh = playerRef.current?.videoHeight;
    if (vw && vh && !initialVideoDimensions) {
      setInitialVideoDimensions({ width: vw, height: vh });
      console.log('📐 Captured initial video dimensions:', { width: vw, height: vh });
    }
  };

  // Function to ensure we capture video dimensions when they become available
  const ensureVideoDimensionsCaptured = () => {
    const vw = playerRef.current?.videoWidth;
    const vh = playerRef.current?.videoHeight;
    if (vw && vh) {
      if (!initialVideoDimensions) {
        setInitialVideoDimensions({ width: vw, height: vh });
        console.log('📐 Captured video dimensions on demand:', { width: vw, height: vh });
      }
      return true;
    }
    return false;
  };

  // Function to get video dimensions from player
  const getVideoDimensions = () => {
    const vw = playerRef.current?.videoWidth;
    const vh = playerRef.current?.videoHeight;
    if (vw && vh) {
      if (!initialVideoDimensions) {
        setInitialVideoDimensions({ width: vw, height: vh });
        console.log('📐 Stored video dimensions:', { width: vw, height: vh });
      }
      return { width: vw, height: vh };
    }
    return null;
  };

  // Function to calculate current display dimensions based on window size
  const calculateDisplayDimensions = () => {
    const isSmallScreen = window.innerWidth <= 1440;
    return {
      width: isSmallScreen ? 960 : VIDEO_WIDTH,
      height: isSmallScreen ? 540 : VIDEO_HEIGHT
    };
  };

  // Function to update display dimensions
  const updateDisplayDimensions = () => {
    const newDimensions = calculateDisplayDimensions();
    setDisplayDimensions(newDimensions);
  };


  // Function to process video with APIs when triggered by button (Action A)
  const processVideoWithAPIs = async (showProcessingUI = true) => {
    // Use the canvas (via playerRef.videoElement) for drawImage-based inference
    const inferenceSource = canvasRef.current;
    if (!inferenceSource || !playerRef.current) {
      return;
    }

    // Prevent duplicate processing
    if (processingInProgressRef.current) {
      console.log('⚠️ Video processing already in progress, skipping');
      return;
    }

    // Check if player is ready
    if (playerRef.current.readyState() < 2) {
      console.warn('⚠️ Video not ready for processing, please wait for video to load');
      return;
    }

    const vw = playerRef.current.videoWidth;
    const vh = playerRef.current.videoHeight;

    try {
      processingInProgressRef.current = true;
      if (showProcessingUI) {
        setIsProcessingCamera(true);
        setHasProcessedVideo(true);
      }

      // Step 1: Get keypoints from keypoint-detector API
      // The predict function calls extractFrameAsBase64 which does ctx.drawImage(source)
      // A <canvas> element is a valid drawImage source.
      const keypoints = await keypointPredictionService.predict(inferenceSource);

      // Step 2: Get camera parameters from camera-predictor API
      const cameraParams = await cameraPredictionService.predict(keypoints, vw, vh);

      // Store camera parameters for later use when saving
      const bestModel = cameraParams.best_model;
      const bestCameraParams = cameraParams[bestModel];
      setStoredCameraParams(bestCameraParams);

      // Track camera calibration success
      usageTrackingService.trackCameraCalibration('auto', true);

      // Step 2.5: Add predicted keypoints as court annotations for verification
      try {
        // Add predicted keypoints to court annotations
        saveCourtKeypoints(keypoints);
      } catch (error) {
        console.error('❌ Error adding predicted keypoints as court annotation:', error);
      }

      // Step 2.6: Skip reprojected keypoints - they are no longer displayed or saved
      // Reprojected keypoints are only shown if loaded from JSON files

      // Step 3: Convert player annotations to meters using the best camera model (only if checkbox is NOT checked)
      if (!saveCourtCoordinates) {

        // Convert player bounding boxes to real world coordinates
        if (playerAnnotations.length > 0) {
          try {
            // Convert player annotations to the format expected by the API
            const playerAnnotationsForAPI = playerAnnotations.map(annotation => ({
              time: annotation.timestamp,
              timestamp: annotation.timestamp,
              player: annotation.player,
              boundingBox: annotation.boundingBox,
              strokeScore: annotation.strokeScore || 0,
              strokeType: annotation.strokeType || null
            }));

            // Get video dimensions from video element
            const videoDimensions = getVideoDimensions();

            // Always use video element dimensions
            const conversionWidth = videoDimensions ? videoDimensions.width : vw;
            const conversionHeight = videoDimensions ? videoDimensions.height : vh;

            // Store initial video dimensions for use in save function
            if (!initialVideoDimensions) {
              setInitialVideoDimensions({
                width: conversionWidth,
                height: conversionHeight
              });
              console.log('  - Stored initial video dimensions:', { conversionWidth, conversionHeight });
            }

            console.log('  - Final conversion dimensions:', { conversionWidth, conversionHeight });

            const playerConversionResult = await unitConversionService.convertPlayerBoundingBoxes(
              playerAnnotationsForAPI,
              bestCameraParams,
              conversionWidth,
              conversionHeight
            );

            if (playerConversionResult.success) {
              // Update player annotations with meter coordinates
              const convertedPlayerAnnotations = playerConversionResult.converted_annotations;
              const updatedPlayerAnnotations = playerAnnotations.map(annotation => {
                const converted = convertedPlayerAnnotations.find(conv =>
                  conv.time === annotation.timestamp &&
                  conv.player === annotation.player
                );

                if (converted) {
                  return {
                    ...annotation,
                    x_m: converted.x_m,
                    y_m: converted.y_m
                  };
                }

                return annotation;
              });

              // Update the player annotations state with meter coordinates
              setPlayerAnnotations(updatedPlayerAnnotations);
            } else {
              console.warn('⚠️ Player bounding box conversion failed:', playerConversionResult.error);
            }
          } catch (conversionError) {
            console.error('❌ Error converting player bounding boxes to meters:', conversionError);
            // Don't fail the entire process if player conversion fails
          }
        }
      }
    } catch (error) {
      console.error('❌ Error processing video with APIs:', error);
      if (showProcessingUI) {
        setHasProcessedVideo(false); // Reset on error to allow retry
      }
    } finally {
      processingInProgressRef.current = false;
      if (showProcessingUI) {
        setIsProcessingCamera(false);
      }
    }
  };

  const {
    annotations,
    playerAnnotations,
    annotationMode,
    currentAnnotations,
    annotationsByFrame,
    playerAnnotationsByFrame,
    annotationCounts,
    annotationProgress,
    reviewCounts,
    customLabels,
    addAnnotation,
    removeAnnotation,
    addPlayerAnnotation,
    addPlayerAnnotationWithMeters,
    removePlayerAnnotation,
    removeAllPlayerAnnotationsAtTime,
    getCurrentPlayerAnnotations,
    switchAnnotationMode,
    rallyGroups,
    navigateToAnnotation,
    navigateToReview,
    navigateToRally,
    navigateToCustomLabel,
    toggleFlagForReview,
    currentStrokeIn,
    currentStrokeInfo,
    setStrokeInCall,
    saveStrokesFile,
    buildStrokeRecordsForSave,
    hasStrokeRecords,
    cycleCustomLabel,
    addCustomLabel,
    removeCustomLabel,
    saveAnnotations,
    loadAnnotations,
    clearAnnotations,
    setAnnotationMode,
    currentPlayer,
    currentStrokeType,
    cyclePlayer,
    cycleShotType,
    setPlayer,
    setStrokeType,
    playerFilter,
    setPlayerFilter,
    strokeTypeFilter,
    setStrokeTypeFilter,
    volleyFilter,
    setVolleyFilter,
    volleyProgress,
    trajectories,
    setAnnotations,
    setPlayerAnnotations,
  } = useAnnotations(
    currentTime,
    playerRef,
    () => {
      // Callback to trigger camera parameter prediction on first annotation
      if (saveCourtCoordinates && !storedCameraParams && !processingInProgressRef.current) {
        processVideoWithAPIs(false); // Background processing
      }
    },
    (reason) => {
      // Callback when upgrade is needed (usage limit reached)
      console.log('Upgrade needed:', reason);
      setIsPlansOpen(true);
    }
  );

  const {
    courtKeypoints: allCourtKeypoints,
    courtKeypointsByFrame,
    setCourtKeypoints,
    videoMode,
    isCourtMode,
    isPlayerMode,
    isBallMode,
    isRallyMode,
    currentCourtKeypoints,
    enterCourtMode,
    exitCourtMode,
    enterPlayerMode,
    exitPlayerMode,
    setVideoModeDirect,
    saveCourtKeypoints,
    removeCourtKeypoints,
    getCourtKeypointCounts,
    saveCourtKeypointsToJSON,
    loadCourtKeypoints,
    clearCourtKeypoints,
    getCurrentCourtKeypoints,
    copyPreviousCourtAnnotation,
  } = useVideoMode(currentTime, playerRef);

  const {
    nearPlayer,
    setNearPlayer,
    farPlayer,
    setFarPlayer,
    initialServer,
    setInitialServer,
    adScoring,
    setAdScoring,
    rallies,
    scoreboard,
    pendingStart,
    awaitingWinnerId,
    incompleteRallies,
    startRally,
    endRally,
    cancelPendingRally,
    setRallyWinner,
    setRallyTime,
    deleteRally,
    saveRalliesToJSON,
    loadRalliesFromFile,
  } = useRallies();

  // Surfaced under the rally mark button; cleared on the next successful mark.
  const [rallyMarkError, setRallyMarkError] = useState('');
  const [rallyFileName, setRallyFileName] = useState('');



  // Focus the container for keyboard events
  useEffect(() => {
    const container = document.querySelector('[tabindex="0"]');
    if (container) {
      container.focus();
    }
  }, []);

  // No automatic FPS loading from URL parameters

  // Initialize display dimensions and handle window resize
  useEffect(() => {
    // Initialize display dimensions
    updateDisplayDimensions();

    // Add resize event listener
    const handleResize = () => {
      updateDisplayDimensions();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Capture initial video dimensions when video loads
  // Capture initial video dimensions when WebCodecs player is ready
  useEffect(() => {
    if (isPlayerReady) {
          captureInitialVideoDimensions();
    }
  }, [isPlayerReady, initialVideoDimensions]);


  // Frame index is now built by the WebCodecsPlayer inside useVideoPlayer.
  // No separate buildFrameIndex effect needed.

  // Update URL parameters when video data changes
  useEffect(() => {
    if (videoUrl) {
      const currentFPS = getFPS();
      encodeVideoToURL(videoUrl, loadedFileName, currentFPS);
    } else {
      // Clear URL parameter if no video
      clearVideoFromURL();
    }
  }, [videoUrl, loadedFileName]);

  // No automatic video loading from URL changes

  // Video processing is now triggered manually when AI Assist is enabled

  // Handle video URL input
  const handleVideoUrlChange = (e) => {
    setVideoUrl(e.target.value);
    // Clear video error when user starts typing
    if (videoError) {
      setVideoError('');
    }
  };

  // Handle local video file upload
  const handleVideoFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if it's a video file
    if (!file.type.startsWith('video/')) {
      setVideoError('Please select a valid video file');
      return;
    }

    try {
      // Clear existing errors
      setVideoError('');

      // Show loading message
      setVideoError('Analyzing video metadata...');

      // Clear existing annotations when loading new video
      clearAnnotations();
      clearCourtKeypoints();
      setHasProcessedVideo(false); // Reset processing state for new video
      processingInProgressRef.current = false; // Reset processing ref
      setStoredCameraParams(null); // Clear stored camera parameters for new video

      // Extract video metadata including frame rate
      console.log('Extracting video metadata...');
      const metadata = await getVideoMetadata(file);
      console.log('Video metadata extracted:', metadata);

      // Create object URL for the video file
      const videoObjectUrl = URL.createObjectURL(file);
      console.log('Created object URL:', videoObjectUrl);

      // Set detected FPS
      setFPS(metadata.fps);

      // Set the video URL to the object URL
      setVideoUrl(videoObjectUrl);
      setProcessedVideoUrl(videoObjectUrl);

      // Store the file reference
      setLocalVideoFile(file);

      // Set the loaded filename for display and persistence
      setLoadedFileName(file.name);

      // Track video loaded from file
      usageTrackingService.trackVideoLoaded('file', file.name);

      // Clear loading message
      setVideoError('');

    } catch (error) {
      console.error('Error uploading video file:', error);
      setVideoError(`Error uploading video: ${error.message}`);
    }

    // Reset file input so the same file can be uploaded again if needed
    e.target.value = '';
  };

  // Helper function to load video with mediainfo FPS extraction
  const loadVideoWithMediaInfo = async (videoUrl, filename = null) => {
    try {
      // Clear existing errors
      setVideoError('');

      // Clear existing annotations when loading new video
      clearAnnotations();
      clearCourtKeypoints();
      setHasProcessedVideo(false); // Reset processing state for new video
      processingInProgressRef.current = false; // Reset processing ref
      setStoredCameraParams(null); // Clear stored camera parameters for new video

      let finalVideoUrl = videoUrl.trim();
      let newFPS = null;

      // Check if this is the new format with FPS information
      if (isVideoFormat(videoUrl)) {
        const parsed = parseVideoString(videoUrl);

        if (parsed && parsed.videoUrl && parsed.fpsValue) {
          finalVideoUrl = parsed.videoUrl;

          // Parse and set the FPS - this is required for the new format
          try {
            newFPS = parseFPS(parsed.fpsValue);
            setFPS(newFPS);
          } catch (error) {
            console.error('Failed to parse FPS:', error);
            setVideoError(`Failed to load video: ${error.message}`);
            return; // Don't load the video if FPS is missing
          }
        } else {
          setVideoError('Invalid video format. Expected: VIDEO:url,FPS:value');
          return;
        }
      } else {
        // No VIDEO:url,FPS:value format — FPS will be extracted automatically
        // by WebCodecsPlayer during init (via MP4Box.js parsing), and the global
        // FPS will be set by useVideoPlayer after the player is ready.
        console.log('No explicit FPS in URL — WebCodecsPlayer will extract FPS from MP4 container');
      }

      // Set the video URL for the button state and the processed video URL for the video player
      setVideoUrl(finalVideoUrl);
      setProcessedVideoUrl(finalVideoUrl);

      // Extract and set filename for remote videos
      let loadedFilename = '';
      if (filename) {
        setLoadedFileName(filename);
        loadedFilename = filename;
      } else {
        try {
          const urlWithoutParams = finalVideoUrl.split('?')[0];
          const urlParts = urlWithoutParams.split('/');
          const extractedFilename = urlParts[urlParts.length - 1];
          if (extractedFilename && extractedFilename.includes('.')) {
            setLoadedFileName(extractedFilename);
            loadedFilename = extractedFilename;
          }
        } catch (filenameError) {
          console.warn('Could not extract filename from URL:', filenameError);
        }
      }

      // Track video loaded
      usageTrackingService.trackVideoLoaded('url', loadedFilename);

      // Note: Camera parameter prediction will be triggered on first annotation
      // instead of immediately after video load

    } catch (error) {
      console.error('Error loading video:', error);
      setVideoError(`Error loading video: ${error.message}`);
    }
  };

  const handleVideoUrlSubmit = async (e) => {
    e.preventDefault();
    if (!videoUrl.trim()) {
      return;
    }

    await loadVideoWithMediaInfo(videoUrl);
  };

  // Handle annotation file upload - parses JSON once (via Web Worker for large files)
  // and passes pre-parsed data to loadAnnotations to avoid redundant parsing
  const handleAnnotationUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      // Read file text once
      const text = await file.text();
      // Parse once using Web Worker for large files (>5MB)
      const rawData = await parseJSONAsync(text);
      let success = false;

      // Check if this is the new stroke format (array of stroke objects)
      const isNewStrokeFormat = Array.isArray(rawData) &&
        rawData.length > 0 &&
        rawData[0].player_x1_px !== undefined &&
        rawData[0].opponent_x1_px !== undefined;

      // Pass pre-parsed data directly to loadAnnotations (no re-parsing!)
      if (isNewStrokeFormat) {
        const result = await loadAnnotations(rawData);
        if (result && result.success) {
          success = true;
        }
      } else {
        // Old format: object with properties
        const data = rawData;

        // Load ball annotations if they exist
        if (data.ball_annotations && Array.isArray(data.ball_annotations)) {
          // Pass pre-parsed data with annotations key directly
          const ballData = { ...data, annotations: data.ball_annotations };
          const ballResult = await loadAnnotations(ballData);
          if (ballResult && ballResult.success) {
            success = true;
          }
        }

        // Load court keypoints if they exist
        if (data.court_keypoints && Array.isArray(data.court_keypoints)) {
          // Create a file for loadCourtKeypoints (it still expects a File)
          const courtBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
          const courtFile = new File([courtBlob], file.name, { type: 'application/json' });

          const courtResult = await loadCourtKeypoints(courtFile);
          if (courtResult && courtResult.success) {
            success = true;
          }
        }

        // Load camera parameters if they exist
        if (data.camera_parameters) {
          setStoredCameraParams(data.camera_parameters);
          // Cancel any ongoing prediction since we now have camera parameters
          processingInProgressRef.current = false;
          success = true;
        }


        // If neither exists, try the old format
        if (!data.ball_annotations && !data.court_keypoints && data.annotations) {
          const result = await loadAnnotations(data);
          if (result && result.success) {
            success = true;
          }
        }
      }

      if (success) {
        setLoadedFileName(file.name);
        setAnnotationFileName(file.name);
        setUploadError('');

        // Track annotations loaded
        usageTrackingService.trackAnnotationsLoaded(
          annotations.length,
          playerAnnotations.length,
          allCourtKeypoints.filter(court => !court.reprojected).length
        );
      } else {
        setUploadError('Invalid annotation file format.');
        setLoadedFileName('');
        setAnnotationFileName('');
      }

    } catch (error) {
      setUploadError('Error loading annotation file.');
      setLoadedFileName('');
      setAnnotationFileName('');
    }

    // Reset file input so the same file can be uploaded again if needed
    e.target.value = '';
  };

  const handleClearAnnotations = () => {
    clearAnnotations();
    clearCourtKeypoints();
    setLoadedFileName('');
    setAnnotationFileName('');
    setUploadError('');
    setHasProcessedVideo(false); // Reset processing state
    processingInProgressRef.current = false; // Reset processing ref
    setStoredCameraParams(null); // Clear stored camera parameters
    setInitialVideoDimensions(null); // Clear initial video dimensions
  };

  // Track mouse position using EXACT same logic as ball annotation clicks
  const handleVideoWrapperMouseMove = (e) => {
    if (!isPlayerMode || !isPaused) {
      setMousePosition({ x: null, y: null });
      return;
    }

    // Use EXACT same logic as handleAnnotationClick
    const rect = e.currentTarget.getBoundingClientRect();
    const nativeWidth = playerRef.current?.videoWidth || VIDEO_WIDTH;
    const nativeHeight = playerRef.current?.videoHeight || VIDEO_HEIGHT;

    // Convert mouse position to video coordinates (same as handleAnnotationClick)
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const videoX = (mouseX + pan.x) / zoom;
    const videoY = (mouseY + pan.y) / zoom;

    // Clamp coordinates to display bounds
    const clampedX = Math.max(0, Math.min(videoX, displayDimensions.width));
    const clampedY = Math.max(0, Math.min(videoY, displayDimensions.height));

    // Convert to NATIVE coordinates (EXACTLY like ball annotations)
    const { x: nativeX, y: nativeY } = convertToNativeCoordinates(
      clampedX, clampedY, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
    );

    // Store NATIVE coordinates (like ball annotations), not display coordinates!
    setMousePosition({ x: nativeX, y: nativeY });
  };

  // Mouse wheel for zoom (mouse-centered)
  const handleWheel = (e) => {
    if (!isPaused) return;
    e.preventDefault();

    let newZoom = zoom + (e.deltaY < 0 ? 0.1 : -0.1);
    newZoom = Math.max(1, Math.min(10, newZoom));
    if (newZoom === zoom) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate the video-space coordinates under the mouse before zoom
    const videoX = (mouseX + pan.x) / zoom;
    const videoY = (mouseY + pan.y) / zoom;

    // Calculate new pan so that (videoX, videoY) stays under the mouse
    let newPanX = videoX * newZoom - mouseX;
    let newPanY = videoY * newZoom - mouseY;

    // Clamp pan so you can't see outside the video
    const maxPanX = Math.max(0, displayDimensions.width * (newZoom - 1));
    const maxPanY = Math.max(0, displayDimensions.height * (newZoom - 1));
    newPanX = Math.max(0, Math.min(newPanX, maxPanX));
    newPanY = Math.max(0, Math.min(newPanY, maxPanY));

    setZoom(newZoom);
    setPan({ x: newPanX, y: newPanY });
  };

  // Helper function to infer stroke type based on ball position and player bounding box
  const inferStrokeType = (ballX, ballY, player, playerBoundingBox) => {
    if (!playerBoundingBox) return null;

    const { x1, y1, x2, y2 } = playerBoundingBox;
    const playerCenterX = (x1 + x2) / 2;
    const playerHeight = Math.abs(y2 - y1);
    const playerTopY = Math.min(y1, y2);

    // Check if ball is 20% higher than player's height (serve detection)
    const heightThreshold = playerTopY - (playerHeight * 0.2);
    if (ballY < heightThreshold) {
      return 'serve';
    }

    // Determine forehand/backhand based on left/right position relative to player
    const ballRelativeToPlayer = ballX - playerCenterX;

    if (player === 'bottom') {
      // Bottom player: right = forehand, left = backhand
      return ballRelativeToPlayer > 0 ? 'forehand' : 'backhand';
    } else {
      // Top player: left = forehand, right = backhand (opposite view)
      return ballRelativeToPlayer < 0 ? 'forehand' : 'backhand';
    }
  };

  // Enhanced annotation function with stroke inference and automatic player detection
  const addAnnotationWithInference = async (x, y, selectedPlayer) => {
    if (!playerRef.current) return;

    const videoCurrentTime = playerRef.current.currentTime();
    // Use frame index for exact frame↔time mapping (falls back to FPS if index unavailable)
    const frameNumber = getFrameForTime(videoCurrentTime);
    const annotationTime = getTimeForFrame(frameNumber);

    // Find player bounding box at current time (match by frame number)
    const currentPlayerAnnotations = playerAnnotations.filter(ann => {
      const annFrame = getFrameForTime(ann.timestamp);
      return annFrame === frameNumber && ann.player === selectedPlayer;
    });

    // If no player annotation exists and we have camera parameters, detect players automatically
    if (currentPlayerAnnotations.length === 0 && storedCameraParams) {
      try {
        // Use the canvas for player detection (valid drawImage source)
        const inferenceEl = canvasRef.current;
        if (!inferenceEl) {
          console.warn('No canvas available for player detection');
          // Fall through to original inference logic
        } else {
          // Detect players in current frame
          const detectedPlayers = await playerDetectionService.detect(inferenceEl, 0.5);

          // Note: Usage tracking is done AFTER filtering to count only actual players (max 2)
          // not all raw detections (which may include spectators, ball boys, etc.)

          if (detectedPlayers.length > 0) {
            // Get video dimensions
            const videoWidth = playerRef.current?.videoWidth || 1280;
            const videoHeight = playerRef.current?.videoHeight || 720;

            // Create a pixel-to-meters conversion function using current camera parameters
            const pixelToMetersFn = async (px, py) => {
              try {
                // Use the unit conversion service (with auth) to convert arbitrary points
                const data = await unitConversionService.convertPoints(
                  [{ x: px, y: py }],
                  storedCameraParams,
                  videoWidth,
                  videoHeight
                );

                // Extract the converted point
                if (data.success && data.points && data.points.length > 0) {
                  return {
                    x: data.points[0].x,
                    y: data.points[0].y,
                    z: data.points[0].z
                  };
                }
                return null;
              } catch (error) {
                console.error('Error converting pixel to meters:', error);
                return null;
              }
            };

            // Filter players to get top and bottom
            const filteredPlayers = await playerDetectionService.filterPlayers(
              detectedPlayers,
              pixelToMetersFn,
              videoWidth,
              videoHeight
            );

            // Note: Usage tracking is handled by addPlayerAnnotationWithMeters/addPlayerAnnotation
            // calls below, so we don't call trackPlayerDetection here to avoid double-counting

            // Add player annotations for top and bottom players with meter coordinates
            if (filteredPlayers.top) {
              const topBox = {
                x1: filteredPlayers.top.x1,
                y1: filteredPlayers.top.y1,
                x2: filteredPlayers.top.x2,
                y2: filteredPlayers.top.y2
              };

              // Calculate player feet position and convert to meters
              const feetX = (topBox.x1 + topBox.x2) / 2;
              const feetY = topBox.y2;
              const topMeterCoords = await pixelToMetersFn(feetX, feetY);

              // Add annotation with meter coordinates if conversion succeeded
              if (topMeterCoords) {
                addPlayerAnnotationWithMeters('top', topBox, topMeterCoords.x, topMeterCoords.y);
              } else {
                addPlayerAnnotation('top', topBox);
              }
            }

            if (filteredPlayers.bottom) {
              const bottomBox = {
                x1: filteredPlayers.bottom.x1,
                y1: filteredPlayers.bottom.y1,
                x2: filteredPlayers.bottom.x2,
                y2: filteredPlayers.bottom.y2
              };

              // Calculate player feet position and convert to meters
              const feetX = (bottomBox.x1 + bottomBox.x2) / 2;
              const feetY = bottomBox.y2;
              const bottomMeterCoords = await pixelToMetersFn(feetX, feetY);

              // Add annotation with meter coordinates if conversion succeeded
              if (bottomMeterCoords) {
                addPlayerAnnotationWithMeters('bottom', bottomBox, bottomMeterCoords.x, bottomMeterCoords.y);
              } else {
                addPlayerAnnotation('bottom', bottomBox);
              }
            }

            // Use the bounding box we just detected directly for inference
            // (Don't try to read from state as it updates asynchronously)
            const detectedPlayerBox = selectedPlayer === 'top' ? filteredPlayers.top : filteredPlayers.bottom;
            if (detectedPlayerBox) {
              const playerBoundingBox = {
                x1: detectedPlayerBox.x1,
                y1: detectedPlayerBox.y1,
                x2: detectedPlayerBox.x2,
                y2: detectedPlayerBox.y2
              };
              const inferredStrokeType = inferStrokeType(x, y, selectedPlayer, playerBoundingBox);
              if (inferredStrokeType) {
                addAnnotation(x, y, annotationMode, selectedPlayer, inferredStrokeType);
                return;
              }
            }
          }
        }
      } catch (error) {
        console.error('❌ Automatic player detection failed:', error);
        // Continue with fallback behavior
      }
    }

    // Original inference logic
    let inferredStrokeType = null;
    if (currentPlayerAnnotations.length > 0) {
      // Use the first matching player annotation's bounding box
      const playerBoundingBox = currentPlayerAnnotations[0].boundingBox;
      inferredStrokeType = inferStrokeType(x, y, selectedPlayer, playerBoundingBox);
    }

    if (inferredStrokeType) {
      // Use inferred stroke type and player
      addAnnotation(x, y, annotationMode, selectedPlayer, inferredStrokeType);
    } else {
      // Fallback: pass selectedPlayer even when no bounding box exists
      addAnnotation(x, y, annotationMode, selectedPlayer, currentStrokeType);
    }
  };

  // Handle mouse down on annotation layer (better for right-click detection)
  const handleAnnotationMouseDown = (event) => {
    if (!isPaused) return;

    // Prevent default and stop propagation to ensure event.button is preserved
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const nativeWidth = playerRef.current?.videoWidth || VIDEO_WIDTH;
    const nativeHeight = playerRef.current?.videoHeight || VIDEO_HEIGHT;

    // Convert mouse position to video coordinates (same as CourtOverlay)
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    const videoX = (mouseX + pan.x) / zoom;
    const videoY = (mouseY + pan.y) / zoom;

    // Clamp coordinates to display bounds
    const clampedX = Math.max(0, Math.min(videoX, displayDimensions.width));
    const clampedY = Math.max(0, Math.min(videoY, displayDimensions.height));

    // Convert to native video coordinates
    const { x: nativeX, y: nativeY } = convertToNativeCoordinates(
      clampedX, clampedY, nativeWidth, nativeHeight, displayDimensions.width, displayDimensions.height
    );

    // Determine player based on mouse button
    let selectedPlayer;
    if (event.button === 2) { // Right click
      selectedPlayer = 'top';
    } else { // Left click (button === 0)
      selectedPlayer = 'bottom';
    }

    // Use enhanced annotation with inference
    addAnnotationWithInference(nativeX, nativeY, selectedPlayer);
  };

  // Prevent context menu on right click
  const handleContextMenu = (event) => {
    event.preventDefault();
  };

  // Helper function to check if player exists at current time
  const hasPlayerAtCurrentTime = (player) => {
    if (!playerRef.current) return false;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    return playerAnnotations.some(ann =>
      getFrameForTime(ann.timestamp) === currentFrame && ann.player === player
    );
  };

  // Helper function to add default player bounding box
  const addDefaultPlayerBoundingBox = (player, mouseCoords = null) => {
    if (!playerRef.current) return;

    const nativeWidth = playerRef.current?.videoWidth || 1280;
    const nativeHeight = playerRef.current?.videoHeight || 720;

    // Work entirely in NATIVE coordinate space (like ball annotations)
    // Simple fixed dimensions: 50 pixels right, 100 pixels down
    const boxWidth = 50;
    const boxHeight = 100;

    let nativeBox;

    if (mouseCoords && mouseCoords.x !== null && mouseCoords.y !== null) {
      // Use mouse position as top-left corner (x1, y1) in NATIVE coordinates
      // Box extends 50px right and 100px down from cursor
      nativeBox = {
        x1: mouseCoords.x,
        y1: mouseCoords.y,
        x2: Math.min(mouseCoords.x + boxWidth, nativeWidth),   // Clamp to native width
        y2: Math.min(mouseCoords.y + boxHeight, nativeHeight)  // Clamp to native height
      };
    } else {
      // Fallback to original fixed positioning (in native coordinates)
      const centerX = nativeWidth * 0.5;
      const topPlayerY = nativeHeight * 0.25;    // Upper quarter
      const bottomPlayerY = nativeHeight * 0.75; // Lower quarter

      nativeBox = {
        x1: centerX - boxWidth / 2,
        x2: centerX + boxWidth / 2,
        y1: (player === 'top' ? topPlayerY : bottomPlayerY) - boxHeight / 2,
        y2: (player === 'top' ? topPlayerY : bottomPlayerY) + boxHeight / 2
      };
    }

    addPlayerAnnotation(player, nativeBox);
  };

  // --- Rally scoring -------------------------------------------------------

  const handleStartRally = useCallback(() => {
    if (!playerRef.current) return;
    if (!nearPlayer.trim() || !farPlayer.trim()) {
      setRallyMarkError('Enter both player names before starting.');
      return;
    }
    if (initialServer === null) {
      setRallyMarkError('Pick the starting server before starting.');
      return;
    }
    setRallyMarkError('');
    startRally(playerRef.current.currentTime());
  }, [startRally, nearPlayer, farPlayer, initialServer]);

  const handleEndRally = useCallback(() => {
    if (!playerRef.current) return;
    setRallyMarkError(endRally(playerRef.current.currentTime()) || '');
  }, [endRally]);

  // One key/button drives both halves of the loop: mark the start, play, then
  // mark the end at the pause point.
  const handleToggleRallyMark = useCallback(() => {
    if (pendingStart === null) handleStartRally();
    else handleEndRally();
  }, [pendingStart, handleStartRally, handleEndRally]);

  const handleSetRallyTime = useCallback((id, edge) => {
    if (!playerRef.current) return;
    setRallyTime(id, edge, playerRef.current.currentTime());
  }, [setRallyTime]);

  const handleSaveRallies = useCallback(() => {
    setRallyMarkError(saveRalliesToJSON(videoUrl) || '');
  }, [saveRalliesToJSON, videoUrl]);

  const handleLoadRallies = useCallback(async (file) => {
    const result = await loadRalliesFromFile(file);
    setRallyFileName(result.success ? result.filename : '');
    setRallyMarkError(result.success ? '' : result.error);
  }, [loadRalliesFromFile]);

  // Assign a winner to the rally that is waiting for one — the one just ended —
  // so the labeller can score without leaving the keyboard.
  const handleAwaitingWinner = useCallback((side) => {
    if (!awaitingWinnerId) return;
    setRallyWinner(awaitingWinnerId, side);
  }, [awaitingWinnerId, setRallyWinner]);

  // Keyboard shortcuts for annotations and court mode
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Disable all keyboard shortcuts when popups are open or typing in an input
      if (isAzurePopupOpen || isBillingOpen || isPlansOpen || isSendVideoPopupOpen) {
        return;
      }
      const tag = event.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }

      // Handle Shift+K for shortcuts overlay (global, works even without video)
      if (event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        event.stopPropagation();
        setShowShortcuts(!showShortcuts);
        return;
      }

      if (!playerRef.current) return;

      switch (event.key.toLowerCase()) {
        case KEYBOARD_SHORTCUTS.SWITCH_MODE:
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode) {
            switchAnnotationMode();
          }
          break;
        case 'y':
        case 'Y':
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode && annotationMode === 'stroke') {
            setPlayer('top');
          }
          break;
        case 'u':
        case 'U':
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode && annotationMode === 'stroke') {
            setPlayer('bottom');
          }
          break;
        case 'h':
        case 'H':
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode && annotationMode === 'stroke') {
            setStrokeType('forehand');
          }
          break;
        case 'j':
        case 'J':
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode && annotationMode === 'stroke') {
            setStrokeType('backhand');
          }
          break;
        case 'k':
        case 'K':
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode && annotationMode === 'stroke') {
            setStrokeType('serve');
          }
          break;
        case 'l':
        case 'L':
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode && annotationMode === 'stroke') {
            setStrokeType('smash');
          }
          break;
        case KEYBOARD_SHORTCUTS.BALL_NOT_IN_FRAME:
          event.preventDefault();
          event.stopPropagation();
          if (isPaused && isBallMode) {
            addAnnotation(BALL_NOT_IN_FRAME, BALL_NOT_IN_FRAME, annotationMode, currentPlayer, currentStrokeType);
          }
          break;
        case KEYBOARD_SHORTCUTS.CYCLE_CUSTOM_LABEL:
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode && customLabels.length > 0) {
            cycleCustomLabel();
          }
          break;
        case KEYBOARD_SHORTCUTS.TOGGLE_FLAG_FOR_REVIEW:
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode) {
            toggleFlagForReview();
          }
          break;
        case KEYBOARD_SHORTCUTS.SET_STROKE_IN:
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode) {
            setStrokeInCall(true);
          }
          break;
        case KEYBOARD_SHORTCUTS.SET_STROKE_OUT:
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode) {
            setStrokeInCall(false);
          }
          break;

        case 'x':
        case 'X':
          event.preventDefault();
          event.stopPropagation();
          if (isBallMode) {
            removeAnnotation();
          } else if (isCourtMode) {
            removeCourtKeypoints();
          }
          break;
        case 's':
        case 'S':
          event.preventDefault();
          event.stopPropagation();
          // S round-trips the uploaded strokes file so its structure survives the edit.
          // Shift+S forces the all-annotations export, which is the only format that
          // carries court keypoints, player boxes and strokes added inside the app.
          if (isRallyMode) {
            handleSaveRallies();
          } else if (!isPlayerMode) {
            if (hasStrokeRecords && !event.shiftKey) {
              saveStrokesFile(annotationFileName, annotatorName);
            } else {
              saveAllAnnotations(videoUrl, videoDuration);
            }
          }
          break;
        case KEYBOARD_SHORTCUTS.COURT_MODE:
          event.preventDefault();
          event.stopPropagation();
          if (isPaused) {
            if (event.ctrlKey) {
              // Ctrl+C to copy previous court annotation
              if (isCourtMode) {
                const success = copyPreviousCourtAnnotation();
                if (success) {
                } else {
                  console.warn('No previous court annotation found to copy');
                }
              }
            } else if (isCourtMode) {
              // C to predict court keypoints in court mode
              predictCourtKeypoints();
            }
            // Removed: C key no longer enters court mode, only predicts when already in court mode
          }
          break;
        case 'b':
        case 'B':
          event.preventDefault();
          event.stopPropagation();
          if (isPlayerMode && isPaused) {
            if (event.shiftKey) {
              // Shift+B: Remove bottom player bounding box
              removePlayerAnnotation('bottom');
            } else {
              // B: Add bottom player bounding box
              if (!hasPlayerAtCurrentTime('bottom')) {
                addDefaultPlayerBoundingBox('bottom', mousePosition);
              }
            }
          }
          break;
        case 't':
        case 'T':
          event.preventDefault();
          event.stopPropagation();
          if (isPlayerMode && isPaused) {
            if (event.shiftKey) {
              // Shift+T: Remove top player bounding box
              removePlayerAnnotation('top');
            } else {
              // T: Add top player bounding box
              if (!hasPlayerAtCurrentTime('top')) {
                addDefaultPlayerBoundingBox('top', mousePosition);
              }
            }
          }
          break;
        case KEYBOARD_SHORTCUTS.RALLY_MARK:
          if (!isRallyMode) break;
          event.preventDefault();
          event.stopPropagation();
          handleToggleRallyMark();
          break;
        case KEYBOARD_SHORTCUTS.RALLY_WINNER_NEAR:
          if (!isRallyMode) break;
          event.preventDefault();
          event.stopPropagation();
          handleAwaitingWinner('near');
          break;
        case KEYBOARD_SHORTCUTS.RALLY_WINNER_FAR:
          if (!isRallyMode) break;
          event.preventDefault();
          event.stopPropagation();
          handleAwaitingWinner('far');
          break;
        case 'escape':
          event.preventDefault();
          event.stopPropagation();
          if (isRallyMode) {
            // Escape backs out of a mis-started rally first; only a second
            // press leaves the mode, so the labeller can't lose work by reflex.
            if (pendingStart !== null) cancelPendingRally();
            else setVideoModeDirect('ball');
          } else if (isCourtMode) {
            setVideoModeDirect('ball');
            // Keep court keypoints when exiting with escape
          } else if (isPlayerMode) {
            setVideoModeDirect('ball');
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [isPaused, currentTime, annotations, videoDuration, isBallMode, isCourtMode, isPlayerMode, isRallyMode, pendingStart, cancelPendingRally, handleToggleRallyMark, handleAwaitingWinner, handleSaveRallies, currentCourtKeypoints, annotationMode, setPlayer, setStrokeType, playerAnnotations, hasPlayerAtCurrentTime, addDefaultPlayerBoundingBox, removePlayerAnnotation, mousePosition, showShortcuts, isAzurePopupOpen, isBillingOpen, isPlansOpen, isSendVideoPopupOpen, customLabels, cycleCustomLabel, toggleFlagForReview, setStrokeInCall, saveStrokesFile, hasStrokeRecords, annotationFileName, annotatorName]);

  // Get stable court keypoints for court mode
  const getStableCourtKeypoints = () => {
    if (!isCourtMode) return null;

    // Only show court keypoints if they exist at current time
    if (currentCourtKeypoints) {
      // Handle both single court (legacy) and multiple courts (new)
      if (Array.isArray(currentCourtKeypoints)) {
        return currentCourtKeypoints;
      } else {
        return currentCourtKeypoints.keypoints;
      }
    }

    return null;
  };

  // Add court keypoints at current time
  const addCourtKeypoints = () => {
    if (!isCourtMode) return;

    const keypoints = getCurrentCourtKeypoints();
    if (keypoints) {
      saveCourtKeypoints(keypoints);
    }
  };

  // Recalibrate camera parameters using current court keypoints
  const recalibrateCameraParameters = async () => {
    if (!playerRef.current) {
      return;
    }
    const rvw = playerRef.current.videoWidth;
    const rvh = playerRef.current.videoHeight;

    // Get current court keypoints using the same method as the overlay
    // This ensures we get the keypoints that are actually being displayed/edited
    // No fallbacks - if keypoints don't exist, fail explicitly
    const stableKeypoints = getStableCourtKeypoints();

    if (!stableKeypoints) {
      return;
    }

    let keypoints = null;

    if (Array.isArray(stableKeypoints)) {
      // Find the primary (non-reprojected) court
      const primaryCourt = stableKeypoints.find(court => !court.reprojected);
      if (!primaryCourt || !primaryCourt.keypoints) {
        return;
      }
      keypoints = primaryCourt.keypoints;
    } else {
      // Single keypoints object (legacy format)
      keypoints = stableKeypoints;
    }

    if (!keypoints) {
      return;
    }

    // Validate keypoints format - should be an object with keys A-R
    const keypointLabels = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R'];
    const hasValidFormat = keypointLabels.some(label =>
      keypoints[label] &&
      typeof keypoints[label] === 'object' &&
      typeof keypoints[label].x === 'number' &&
      typeof keypoints[label].y === 'number'
    );

    if (!hasValidFormat) {
      return;
    }

    // Prevent duplicate processing
    if (processingInProgressRef.current) {
      return;
    }

    try {
      processingInProgressRef.current = true;
      setIsProcessingCamera(true);

      // Get camera parameters from camera-predictor API using current keypoints
      const cameraParams = await cameraPredictionService.predict(keypoints, rvw, rvh);

      // Store the updated camera parameters
      const bestModel = cameraParams.best_model;
      const bestCameraParams = cameraParams[bestModel];
      setStoredCameraParams(bestCameraParams);

      // Track manual camera recalibration
      usageTrackingService.trackCameraCalibration('manual', true);

      // Recalculate real-world coordinates for all existing player annotations
      if (playerAnnotations.length > 0) {
        try {
          // Get video dimensions
          const videoDimensions = getVideoDimensions();
          const conversionWidth = videoDimensions ? videoDimensions.width :
            (initialVideoDimensions ? initialVideoDimensions.width : rvw);
          const conversionHeight = videoDimensions ? videoDimensions.height :
            (initialVideoDimensions ? initialVideoDimensions.height : rvh);

          // Convert all player annotations to the format expected by the API
          const playerAnnotationsForAPI = playerAnnotations.map(annotation => ({
            time: annotation.timestamp,
            timestamp: annotation.timestamp,
            player: annotation.player,
            boundingBox: annotation.boundingBox,
            strokeScore: annotation.strokeScore || 0,
            strokeType: annotation.strokeType || null
          }));

          const playerConversionResult = await unitConversionService.convertPlayerBoundingBoxes(
            playerAnnotationsForAPI,
            bestCameraParams,
            conversionWidth,
            conversionHeight
          );

          if (playerConversionResult.success) {
            // Update player annotations with new meter coordinates
            const convertedPlayerAnnotations = playerConversionResult.converted_annotations;

            const updatedPlayerAnnotations = playerAnnotations.map(annotation => {
              const converted = convertedPlayerAnnotations.find(conv =>
                conv.time === annotation.timestamp &&
                conv.player === annotation.player
              );

              if (converted) {
                return {
                  ...annotation,
                  x_m: converted.x_m,
                  y_m: converted.y_m
                };
              }

              return annotation;
            });

            // Update the player annotations state with new meter coordinates
            setPlayerAnnotations(updatedPlayerAnnotations);
          }
        } catch (error) {
          // Silently fail - camera params are still updated
        }
      }
    } catch (error) {
      // Silently fail - user will see the processing state end
    } finally {
      processingInProgressRef.current = false;
      setIsProcessingCamera(false);
    }
  };

  // Predict court keypoints using ONNX model
  const predictCourtKeypoints = async () => {
    if (!isCourtMode || !canvasRef.current) {
      return;
    }

    try {
      // Get keypoints from canvas (WebCodecs renders to canvas; valid drawImage source)
      const predictedKeypoints = await keypointPredictionService.predict(canvasRef.current);

      // Save the predicted keypoints using existing handler
      handleCourtPrediction(predictedKeypoints);

      // Track court prediction success
      usageTrackingService.trackCourtPrediction(true);

    } catch (error) {
      console.error('Court keypoint prediction failed:', error);

      // Track court prediction failure
      usageTrackingService.trackCourtPrediction(false);
    }
  };

  // Handle court keypoints change
  const handleCourtKeypointsChange = (newKeypoints) => {
    // Save the court keypoints for the current time
    if (newKeypoints) {
      saveCourtKeypoints(newKeypoints);
    }
  };

  // Handle court prediction
  const handleCourtPrediction = (keypoints) => {
    // Save the predicted court keypoints
    if (keypoints) {
      saveCourtKeypoints(keypoints);
    }
  };

  // Handle court keypoints save
  const handleCourtKeypointsSave = (keypoints) => {
    // Save the court keypoints
    if (keypoints) {
      saveCourtKeypoints(keypoints);
    }
  };

  const handleModeToggle = useCallback(() => {
    if (isBallMode) {
      setVideoModeDirect('player');
    } else if (isPlayerMode) {
      setVideoModeDirect('court');
    } else if (isCourtMode) {
      setVideoModeDirect('rally');
    } else if (isRallyMode) {
      setVideoModeDirect('ball');
    }
  }, [isBallMode, isPlayerMode, isCourtMode, isRallyMode, setVideoModeDirect]);

  // Navigate to court keypoint (only non-reprojected courts)
  const navigateToCourtKeypoint = (direction = 'next') => {
    if (!playerRef.current || !allCourtKeypoints.length) return;

    const videoCurrentTime = playerRef.current.currentTime();
    const currentFrame = getFrameForTime(videoCurrentTime);

    // Only navigate between non-reprojected courts
    const nonReprojectedCourts = allCourtKeypoints.filter(court => !court.reprojected);

    let targetCourt;
    if (direction === 'next') {
      targetCourt = nonReprojectedCourts
        .filter(court => getFrameForTime(court.timestamp) > currentFrame)
        .sort((a, b) => a.timestamp - b.timestamp)[0];
    } else {
      targetCourt = nonReprojectedCourts
        .filter(court => getFrameForTime(court.timestamp) < currentFrame)
        .sort((a, b) => b.timestamp - a.timestamp)[0];
    }

    if (targetCourt && playerRef.current) {
      playerRef.current.currentTime(targetCourt.timestamp);
    }
  };

  // Handle opening shortcuts in new tab
  const openShortcutsInNewTab = () => {
    window.open('/shortcuts.html', '_blank', 'width=900,height=700,scrollbars=yes,resizable=yes');
  };

  // Handle saving all annotations
  const saveAllAnnotations = async (videoUrl, videoDuration) => {
    if (!playerRef.current) return;

    const currentFPS = getFPS();
    if (!currentFPS) {
      console.error('Cannot save annotations: FPS not set');
      return;
    }

    // Get video dimensions
    const videoDimensions = getVideoDimensions();
    const pvw = playerRef.current?.videoWidth || 1280;
    const pvh = playerRef.current?.videoHeight || 720;

    // Always define conversion dimensions for use in data object
    // Use initialVideoDimensions if available, otherwise fall back to player or defaults
    const conversionWidth = videoDimensions ? videoDimensions.width :
      (initialVideoDimensions ? initialVideoDimensions.width : pvw);
    const conversionHeight = videoDimensions ? videoDimensions.height :
      (initialVideoDimensions ? initialVideoDimensions.height : pvh);

    // If we have stored camera parameters, convert player annotations to meters
    let annotationsToSave = annotations;
    let playerAnnotationsToSave = playerAnnotations;

    if (storedCameraParams) {
      // Convert player bounding boxes to real world coordinates
      if (playerAnnotations.length > 0) {
        try {
          // Convert all player annotations to the format expected by the API
          const playerAnnotationsForAPI = playerAnnotations.map(annotation => ({
            time: annotation.timestamp,
            timestamp: annotation.timestamp,
            player: annotation.player,
            boundingBox: annotation.boundingBox,
            strokeScore: annotation.strokeScore || 0,
            strokeType: annotation.strokeType || null
          }));


          const playerConversionResult = await unitConversionService.convertPlayerBoundingBoxes(
            playerAnnotationsForAPI,
            storedCameraParams,
            conversionWidth,
            conversionHeight
          );

          if (playerConversionResult.success) {
            // Update player annotations with meter coordinates
            const convertedPlayerAnnotations = playerConversionResult.converted_annotations;

            const updatedPlayerAnnotations = playerAnnotations.map(annotation => {
              const converted = convertedPlayerAnnotations.find(conv =>
                conv.time === annotation.timestamp &&
                conv.player === annotation.player
              );

              if (converted) {
                return {
                  ...annotation,
                  x_m: converted.x_m,
                  y_m: converted.y_m
                };
              }

              return annotation;
            });

            // Update the player annotations state with meter coordinates
            setPlayerAnnotations(updatedPlayerAnnotations);
            playerAnnotationsToSave = updatedPlayerAnnotations;
          } else {
            console.warn('⚠️ Player bounding box conversion failed:', playerConversionResult.error);
            playerAnnotationsToSave = playerAnnotations;
          }
        } catch (conversionError) {
          console.error('❌ Error converting player bounding boxes to meters:', conversionError);
          playerAnnotationsToSave = playerAnnotations;
        }
      } else {
        playerAnnotationsToSave = playerAnnotations;
      }
    }

    // Use loadedFileName state if available, otherwise try to extract from URL
    let videoFilename = '';
    if (loadedFileName) {
      // Remove the file extension for cleaner filenames
      videoFilename = loadedFileName.replace(/\.[^/.]+$/, '');
    } else {
      // Fallback: try to extract from URL
      try {
        if (videoUrl) {
          const urlWithoutParams = videoUrl.split('?')[0];
          const urlParts = urlWithoutParams.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          if (lastPart.toLowerCase().endsWith('.mp4')) {
            // Remove the .mp4 extension for cleaner filenames
            videoFilename = lastPart.slice(0, -4);
          }
        }
      } catch (error) {
        console.warn('Could not extract video filename from URL:', error);
      }
    }

    // Format ball annotations — convert exact PTS back to frame/fps for file compatibility
    const currentFPSForSave = getFPS();
    const ballAnnotationsData = annotationsToSave.map(annotation => {
      const frame = getFrameForTime(annotation.timestamp);
      const fileTime = currentFPSForSave ? frame / currentFPSForSave : annotation.timestamp;
      const baseData = {
        time: fileTime,
        timestamp: formatTime(fileTime),
        x: Math.round(annotation.x),
        y: Math.round(annotation.y),
        type: annotation.type
      };

      // Add meter coordinates if they exist (from camera calibration)
      if (annotation.x_m !== undefined && annotation.y_m !== undefined) {
        baseData.x_m = annotation.x_m;
        baseData.y_m = annotation.y_m;
        if (annotation.z_m !== undefined) {
          baseData.z_m = annotation.z_m;
        }
      }

      // Add bounding box data if it exists
      if (annotation.boundingBox) {
        baseData.boundingBox = {
          x1: Math.round(annotation.boundingBox.x1),
          y1: Math.round(annotation.boundingBox.y1),
          x2: Math.round(annotation.boundingBox.x2),
          y2: Math.round(annotation.boundingBox.y2)
        };
      }

      if (annotation.flaggedForReview) {
        baseData.flaggedForReview = true;
      }
      if (annotation.customLabel && annotation.customLabel !== 'none') {
        baseData.customLabel = annotation.customLabel;
      }

      // Only add player, strokeType and scoring fields for stroke annotations
      if (annotation.type === 'stroke') {
        const strokeData = {
          ...baseData,
          player:     annotation.player     || 'bottom',
          strokeType: annotation.strokeType || 'forehand',
        };
        if (annotation.playerId)   strokeData.playerId   = annotation.playerId;
        if (annotation.pointScore) strokeData.pointScore = annotation.pointScore;
        if (annotation.gameScore)  strokeData.gameScore  = annotation.gameScore;
        if (annotation.setScore)   strokeData.setScore   = annotation.setScore;
        if (annotation.rallyId != null) strokeData.rallyId = annotation.rallyId;
        return strokeData;
      }

      return baseData;
    });

    // Format player annotations
    const playerAnnotationsData = playerAnnotations.map(annotation => {
      const frame = getFrameForTime(annotation.timestamp);
      const fileTime = currentFPSForSave ? frame / currentFPSForSave : annotation.timestamp;
      const baseData = {
        time: fileTime,
        timestamp: formatTime(fileTime),
        player: annotation.player,
        boundingBox: {
          x1: Math.round(annotation.boundingBox.x1),
          y1: Math.round(annotation.boundingBox.y1),
          x2: Math.round(annotation.boundingBox.x2),
          y2: Math.round(annotation.boundingBox.y2)
        },
        strokeScore: annotation.strokeScore || 0,
        strokeType: annotation.strokeType || null
      };

      // Add meter coordinates if they exist (from camera calibration)
      if (annotation.x_m !== undefined && annotation.y_m !== undefined) {
        baseData.x_m = annotation.x_m;
        baseData.y_m = annotation.y_m;
      }

      return baseData;
    });

    // Format court keypoints (filter out reprojected keypoints)
    const courtKeypointsData = allCourtKeypoints
      .filter(court => !court.reprojected)
      .map(court => {
        const frame = getFrameForTime(court.timestamp);
        const fileTime = currentFPSForSave ? frame / currentFPSForSave : court.timestamp;
        return {
          time: fileTime,
          timestamp: formatTime(fileTime),
        keypoints: court.keypoints
        };
      });

    const data = {
      video_filename: videoFilename,
      video_width: conversionWidth,
      video_height: conversionHeight,
      fps: currentFPS,
      total_frames: Math.floor(videoDuration * currentFPS),
      total_duration: formatTime(videoDuration),
      ball_annotations: ballAnnotationsData,
      player_annotations: playerAnnotationsData,
      court_keypoints: courtKeypointsData,
      ...(trajectories.length > 0 && { trajectories }),
    };

    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
    const a = document.createElement('a');
    a.href = url;
    a.download = videoFilename ? `${videoFilename}_all_annotations_${timestamp}.json` : `all_annotations_${timestamp}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Track annotations saved
    usageTrackingService.trackAnnotationsSaved(
      ballAnnotationsData.length,
      playerAnnotationsData.length,
      courtKeypointsData.length
    );
  };

  // Function to generate current annotation data for Azure upload
  // Get the custom label of the annotation at the current frame (for sidebar display)
  const currentAnnotationLabel = useMemo(() => {
    if (!currentAnnotations || currentAnnotations.length === 0) return null;
    return currentAnnotations[0].customLabel || 'none';
  }, [currentAnnotations]);

  // Precompute a sorted timeline of position snapshots — rebuilt only when
  // the annotation file changes, not on every frame.
  const playerPositionTimeline = useMemo(() => {
    const validStrokes = annotations.filter(
      (a) => a.type === 'stroke' && a.strokeType === 'serve' &&
             a.player && a.playerId && a.playerId !== 'None' &&
             (a.player === 'top' || a.player === 'bottom')
    );

    if (validStrokes.length === 0) return [];

    // Identify the two player names
    const playerIdSet = new Set(validStrokes.map((a) => a.playerId));
    const [playerA, playerB] = [...playerIdSet];

    const sorted = [...validStrokes].sort((a, b) => a.timestamp - b.timestamp);

    const snapshots = [];
    let curMap = { top: null, bottom: null };

    for (const stroke of sorted) {
      const hitter   = stroke.player;                                   // 'top' | 'bottom'
      const opponent = hitter === 'top' ? 'bottom' : 'top';
      const otherId  = stroke.playerId === playerA ? playerB : playerA;

      const newTop    = hitter === 'top'    ? stroke.playerId : otherId;
      const newBottom = hitter === 'bottom' ? stroke.playerId : otherId;

      if (newTop !== curMap.top || newBottom !== curMap.bottom) {
        curMap = { top: newTop, bottom: newBottom };
        snapshots.push({ time: stroke.timestamp, map: curMap });
      }
    }

    return snapshots;
  }, [annotations]);

  // Per-frame lookup: O(k) walk over a tiny snapshot list. Returns the same
  // object reference while the position hasn't changed, so AnnotationLayer
  // doesn't see a spurious prop change.
  const playerIdMap = useMemo(() => {
    if (playerPositionTimeline.length === 0) return {};
    let result = playerPositionTimeline[0].map;
    for (const snapshot of playerPositionTimeline) {
      if (snapshot.time <= currentTime) result = snapshot.map;
      else break;
    }
    return result;
  }, [playerPositionTimeline, currentTime]);

  // Stable seek handler for the scoring panel — useCallback with empty deps
  // keeps the reference constant so ScoringTree's React.memo stays effective.
  const handleScoringSeek = useCallback((time) => {
    if (playerRef.current) playerRef.current.currentTime(time);
  }, []);

  // Compute activeStartTime here (not inside ScoringPanel) so that ScoringPanel
  // can be React.memo'd and skip re-renders on every video frame.
  const _scoringTree   = useMemo(() => buildScoringTree(annotations), [annotations]);
  const _scoringPoints = useMemo(() => flattenPoints(_scoringTree),    [_scoringTree]);
  const activeRallyStartTime = useMemo(
    () => getActiveStartTime(_scoringPoints, currentTime),
    [_scoringPoints, currentTime]
  );

  const _normScore = (score) => {
    if (!score) return score;
    const parts = score.split('-').map(Number);
    if (parts.length !== 2 || parts.some(isNaN)) return score;
    const [a, b] = parts;
    return a >= b ? `${a}-${b}` : `${b}-${a}`;
  };

  // Toggle the winner of a single point and propagate the new score through the
  // rest of the game, preserving each subsequent rally's outcome.
  // Uses a numeric fallback (0→15→30→40→4→5…) when the corrected sequence
  // pushes past a standard game-winning score.
  const handleTogglePointWinner = useCallback((point, nextPoint, newOutcome, allGamePoints) => {
    const newNextScore = advanceScoreExtended(point.pointScore, newOutcome);

    const targetIdx = allGamePoints.findIndex(
      p => p.pointScore === nextPoint.pointScore && p.startTime === nextPoint.startTime
    );
    if (targetIdx < 0) return;

    const newScores = allGamePoints.map(p => p.pointScore);
    let running = newNextScore;
    for (let i = targetIdx; i < allGamePoints.length; i++) {
      newScores[i] = running;
      const nextP = allGamePoints[i + 1];
      if (!nextP) break;
      const outcome = getOutcome(allGamePoints[i].pointScore, nextP.pointScore);
      if (!outcome) break;
      running = advanceScoreExtended(running, outcome);
    }

    const ranges = allGamePoints.map((p, i) => ({
      startTime: p.startTime,
      endTime:   allGamePoints[i + 1]?.startTime ?? Infinity,
      newScore:  newScores[i],
      affected:  i >= targetIdx,
    }));

    setAnnotations(prev => prev.map(a => {
      if (a.type !== 'stroke') return a;
      if (_normScore(a.gameScore) !== point.gameScore) return a;
      if (_normScore(a.setScore)  !== point.setScore)  return a;
      const range = ranges.find(
        r => r.affected && a.timestamp >= r.startTime && a.timestamp < r.endTime
      );
      if (range) return { ...a, pointScore: range.newScore };
      return a;
    }));
  }, [setAnnotations]);

  // Insert a new point after Point N by splitting Point N at the current video
  // time. Strokes of Point N at/after currentTime are re-scored to nextPoint's
  // score (they become the new inserted point). Old Point N+1, N+2, … are then
  // propagated forward with "server wins the inserted point" as the new base.
  //
  // Example: Point1="0-0" (t=4:59), serve at t=6:18 still grouped in Point1,
  //          Point2="0-15" (t=10:35).
  //   → Point1 strokes before 6:18 keep "0-0"
  //   → Serve at 6:18 (still in Point1) gets "0-15"  → new Point 2
  //   → Old Point2 strokes get "15-15"               → new Point 3
  // nextPoint may be null when point is the last point in the game.
  const handleAddPointAfter = useCallback((point, nextPoint, allGamePoints) => {
    const currentTimestamp = playerRef.current?.currentTime() ?? 0;

    // When adding after the last point there is no nextPoint to reference, so we
    // derive the inserted score by advancing the current point's score (server wins).
    if (!nextPoint) {
      const insertedScore = advanceScoreExtended(point.pointScore, 'server');
      setAnnotations(prev => {
        const hasStrokes = prev.some(
          a => a.type === 'stroke' &&
            _normScore(a.gameScore) === point.gameScore &&
            _normScore(a.setScore)  === point.setScore  &&
            a.pointScore === point.pointScore &&
            a.timestamp >= currentTimestamp
        );
        if (!hasStrokes) return prev;
        return prev.map(a => {
          if (a.type !== 'stroke') return a;
          if (_normScore(a.gameScore) !== point.gameScore) return a;
          if (_normScore(a.setScore)  !== point.setScore)  return a;
          if (a.pointScore === point.pointScore && a.timestamp >= currentTimestamp) {
            return { ...a, pointScore: insertedScore };
          }
          return a;
        });
      });
      return;
    }

    const nextPointIdx = allGamePoints.findIndex(
      p => p.pointScore === nextPoint.pointScore && p.startTime === nextPoint.startTime
    );
    if (nextPointIdx < 0) return;

    // The inserted point plays at the score the old nextPoint played at.
    const insertedScore = nextPoint.pointScore;
    // After the inserted point (server wins), the score advances.
    const afterInsertScore = advanceScoreExtended(insertedScore, 'server');

    // Pre-compute propagated scores for old Points N+1, N+2, … (nextPointIdx…end).
    const newScores = allGamePoints.map(p => p.pointScore);
    let running = afterInsertScore;
    for (let i = nextPointIdx; i < allGamePoints.length; i++) {
      newScores[i] = running;
      const nextP = allGamePoints[i + 1];
      if (!nextP) break;
      const outcome = getOutcome(allGamePoints[i].pointScore, nextP.pointScore);
      if (!outcome) break;
      running = advanceScoreExtended(running, outcome);
    }

    // Ranges for old Points N+1, N+2, … (for propagation).
    const propagationRanges = [];
    for (let i = nextPointIdx; i < allGamePoints.length; i++) {
      propagationRanges.push({
        startTime: allGamePoints[i].startTime,
        endTime:   allGamePoints[i + 1]?.startTime ?? Infinity,
        newScore:  newScores[i],
      });
    }

    setAnnotations(prev => {
      // Guard: there must be at least one stroke of the CURRENT point at/after
      // currentTime (i.e. something to split off into the new inserted point).
      const hasStrokes = prev.some(
        a => a.type === 'stroke' &&
          _normScore(a.gameScore) === point.gameScore &&
          _normScore(a.setScore)  === point.setScore  &&
          a.pointScore === point.pointScore &&
          a.timestamp >= currentTimestamp
      );
      if (!hasStrokes) return prev;

      return prev.map(a => {
        if (a.type !== 'stroke') return a;
        if (_normScore(a.gameScore) !== point.gameScore) return a;
        if (_normScore(a.setScore)  !== point.setScore)  return a;

        // Strokes of the current point at/after currentTime become the new inserted point.
        if (a.pointScore === point.pointScore && a.timestamp >= currentTimestamp) {
          return { ...a, pointScore: insertedScore };
        }

        // Propagate old Points N+1, N+2, … using their original outcomes.
        const range = propagationRanges.find(
          r => a.timestamp >= r.startTime && a.timestamp < r.endTime
        );
        if (range) return { ...a, pointScore: range.newScore };

        return a;
      });
    });
  }, [setAnnotations]);

  // Change the winner of the last point of a game, which shifts the next game's
  // raw game score. Only the immediate next game's score is updated.
  const handleToggleSetWinner = useCallback((currentSet, nextSet, newOutcome, allSets) => {
    // newOutcome is 'server'|'receiver' from the LAST GAME's server's perspective.
    const lastGame = currentSet.games[currentSet.games.length - 1];
    const newWinnerPlayer = newOutcome === 'server' ? lastGame.serverPlayer : opp(lastGame.serverPlayer);
    const oldWinnerPlayer = computeSetWinner(currentSet, nextSet); // 'top'|'bottom'
    if (!oldWinnerPlayer || newWinnerPlayer === oldWinnerPlayer) return;

    const setIdx = allSets.findIndex(
      s => s.rawSetScore === currentSet.rawSetScore && s.serverPlayer === currentSet.serverPlayer
    );
    if (setIdx < 0) return;

    // Uniform delta: subtract old winner's count, add new winner's count.
    // Raw set scores are unique per set (monotonically increasing), so we
    // match by raw score string — same approach as handleToggleGameWinner.
    const deltaTop    = (newWinnerPlayer === 'top'    ? 1 : 0) - (oldWinnerPlayer === 'top'    ? 1 : 0);
    const deltaBottom = (newWinnerPlayer === 'bottom' ? 1 : 0) - (oldWinnerPlayer === 'bottom' ? 1 : 0);

    // Build oldScore → newScore mapping for every subsequent set.
    const scoreUpdates = allSets.slice(setIdx + 1).map(s => {
      const [lN, rN] = s.rawSetScore.split('-').map(n => parseInt(n, 10));
      const topN    = s.serverPlayer === 'top'    ? lN : rN;
      const bottomN = s.serverPlayer === 'bottom' ? lN : rN;
      const newTop    = topN    + deltaTop;
      const newBottom = bottomN + deltaBottom;
      const newLeft   = s.serverPlayer === 'top'    ? newTop    : newBottom;
      const newRight  = s.serverPlayer === 'top'    ? newBottom : newTop;
      return { oldScore: s.rawSetScore, newScore: `${newLeft}-${newRight}` };
    });

    setAnnotations(prev => prev.map(a => {
      if (a.type !== 'stroke') return a;
      const update = scoreUpdates.find(u => u.oldScore === a.setScore);
      return update ? { ...a, setScore: update.newScore } : a;
    }));
  }, [setAnnotations]);

  const handleToggleGameWinner = useCallback((game, nextGame, newOutcome) => {
    const newRawGameScore = computeNewNextGameScore(game, nextGame, newOutcome);
    setAnnotations(prev => prev.map(a => {
      if (a.type !== 'stroke') return a;
      if (_normScore(a.setScore) !== nextGame.setScore) return a;
      // Match by raw gameScore to avoid normalization ambiguity
      if (a.gameScore !== nextGame.rawGameScore) return a;
      return { ...a, gameScore: newRawGameScore };
    }));
  }, [setAnnotations]);

  // Merge a point with the previous point and propagate the score correction
  // to all subsequent points in the same game, preserving each point's outcome
  // (server/receiver won). allGamePoints is the ordered point list from the tree.
  const handleMergePointWithPrevious = useCallback((point, prevPoint, allGamePoints) => {
    const targetIdx = allGamePoints.findIndex(
      p => p.pointScore === point.pointScore && p.startTime === point.startTime
    );
    if (targetIdx < 1) return;

    // Build new scores for targetIdx…end, keeping outcomes from the original sequence.
    const newScores = allGamePoints.map(p => p.pointScore); // copy
    let running = prevPoint.pointScore;
    for (let i = targetIdx; i < allGamePoints.length; i++) {
      newScores[i] = running;
      const nextP = allGamePoints[i + 1];
      if (!nextP) break;
      const outcome = getOutcome(allGamePoints[i].pointScore, nextP.pointScore);
      if (!outcome) break;
      running = advanceScoreExtended(running, outcome);
    }

    // Build ranges: annotations with timestamp in [startTime_i, startTime_{i+1})
    // belong to point i. We use timestamps (not just pointScore) to correctly
    // handle repeated scores like multiple deuces.
    const ranges = allGamePoints.map((p, i) => ({
      startTime: p.startTime,
      endTime:   allGamePoints[i + 1]?.startTime ?? Infinity,
      newScore:  newScores[i],
      affected:  i >= targetIdx,
    }));

    setAnnotations(prev => prev.map(a => {
      if (a.type !== 'stroke') return a;
      if (_normScore(a.gameScore) !== point.gameScore) return a;
      if (_normScore(a.setScore)  !== point.setScore)  return a;
      const range = ranges.find(
        r => r.affected && a.timestamp >= r.startTime && a.timestamp < r.endTime
      );
      if (range) return { ...a, pointScore: range.newScore };
      return a;
    }));
  }, [setAnnotations]);

  // Split Game N after a given point: the clicked point stays in Game N; all
  // subsequent points become a NEW Game N+1.  Old Game N+1, N+2, … are renamed
  // Game N+2, N+3, … and their raw game scores are re-propagated through the set.
  //
  // Strokes are matched by TIMESTAMP RANGE (not by gameScore string) to avoid
  // collisions caused by score normalization (e.g. "1-0" and "0-1" both normalise
  // to "1-0") and because splitScore is mathematically equal to nextGame.rawGameScore
  // in consistent data — so a game-score-based match would corrupt both games.
  //
  // Two-pass approach inside setAnnotations:
  //   Pass 1 – shift old Game N+1, N+2, … (by time range) to their new scores.
  //   Pass 2 – assign splitScore to the split-off strokes (Game N after boundary).
  const handleMergeGameWithPrevious = useCallback((game, prevGame, setGames) => {
    // game = Game N being merged INTO prevGame = Game N-1.
    const gameIdx = setGames.findIndex(
      g => g.rawGameScore === game.rawGameScore && g.serverPlayer === game.serverPlayer
    );
    if (gameIdx <= 0) return;

    // Time ranges for all games (timestamp-based, avoids score-string collisions).
    const timeRanges = setGames.map((g, i) => ({
      startTime: g.points[0].startTime,
      endTime:   setGames[i + 1]?.points[0].startTime ?? Infinity,
    }));
    const gameNRange = timeRanges[gameIdx];

    // Determine the starting point score for Game N's points, continuing from
    // where prevGame left off. We infer the outcome of prevGame's last rally
    // from its last point score (who's leading is likely who won).
    const prevGameLastPoint = prevGame.points[prevGame.points.length - 1];
    const parseP = (t) => ({ '0': 0, '15': 1, '30': 2, '40': 3, 'A': 4 }[t] ?? (parseInt(t, 10) || 0));
    const inferWinner = (ps) => {
      const [s = '0', r = '0'] = ps.split('-');
      return parseP(s) >= parseP(r) ? 'server' : 'receiver';
    };
    const lastPrevOutcome  = inferWinner(prevGameLastPoint.pointScore);
    const firstMergedScore = advanceScoreExtended(prevGameLastPoint.pointScore, lastPrevOutcome);

    // Recompute point scores for Game N's points, starting from firstMergedScore
    // and applying the original outcomes derived from consecutive point scores.
    const gameNPoints = game.points;
    const newPointScores = [firstMergedScore];
    for (let i = 0; i < gameNPoints.length - 1; i++) {
      const outcome = getOutcome(gameNPoints[i].pointScore, gameNPoints[i + 1].pointScore);
      if (!outcome) break;
      newPointScores.push(advanceScoreExtended(newPointScores[newPointScores.length - 1], outcome));
    }

    // Build per-point time ranges for per-stroke pointScore assignment.
    const gameNPointRanges = gameNPoints.map((p, i) => ({
      startTime:     p.startTime,
      endTime:       gameNPoints[i + 1]?.startTime ?? gameNRange.endTime,
      newPointScore: newPointScores[i] ?? firstMergedScore,
    }));

    // Who won prevGame (Game N-1)? This win is being "removed" from the cumulative
    // count because we're collapsing two games into one.
    const winnerPrev = computeGameWinner(prevGame, game) ?? 'server';
    const winnerPrevPlayer = winnerPrev === 'server' ? prevGame.serverPlayer : opp(prevGame.serverPlayer);

    // Cascade: subtract 1 win for winnerPrevPlayer from old Game N+1, N+2, …
    // (Merging 2 games → 1 removes one cumulative win from the set score counts.)
    const cascadeUpdates = setGames.slice(gameIdx + 1).map((g, relIdx) => {
      const [lN, rN] = g.rawGameScore.split('-').map(n => parseInt(n, 10));
      const topN    = g.serverPlayer === 'top'    ? lN : rN;
      const bottomN = g.serverPlayer === 'bottom' ? lN : rN;
      const newTop    = topN    - (winnerPrevPlayer === 'top'    ? 1 : 0);
      const newBottom = bottomN - (winnerPrevPlayer === 'bottom' ? 1 : 0);
      const newLeft   = g.serverPlayer === 'top'    ? newTop    : newBottom;
      const newRight  = g.serverPlayer === 'top'    ? newBottom : newTop;
      return {
        startTime: timeRanges[gameIdx + 1 + relIdx].startTime,
        endTime:   timeRanges[gameIdx + 1 + relIdx].endTime,
        newScore:  `${newLeft}-${newRight}`,
      };
    });

    // Players switch sides after every odd-numbered game (0-indexed: after game 0, 2, 4, …).
    // A switch occurs between game gi-1 and game gi when gi is odd.
    // Exception: when prevGame is a tiebreak (gameScore === '6-6') sides switch every 6
    // tiebreak points, not at game boundaries. Only flip if the merge boundary (i.e. the
    // number of points already in prevGame) lands exactly on a 6-point interval.
    const isTiebreakMerge = prevGame.gameScore === '6-6';
    const shouldFlipPlayers = isTiebreakMerge
      ? prevGame.points.length % 6 === 0
      : gameIdx % 2 === 1;

    setAnnotations(prev => {
      // Pass 1: update subsequent game scores (subtract the removed win).
      const pass1 = cascadeUpdates.length > 0
        ? prev.map(a => {
            if (a.type !== 'stroke') return a;
            if (_normScore(a.setScore) !== game.setScore) return a;
            const hit = cascadeUpdates.find(
              u => a.timestamp >= u.startTime && a.timestamp < u.endTime
            );
            return hit ? { ...a, gameScore: hit.newScore } : a;
          })
        : prev;

      // Pass 2: absorb Game N into prevGame — update gameScore, pointScores, and
      // (if there was a side switch between the two games) flip player/playerId.
      // Precompute the top↔bottom playerId mapping from Game N's strokes.
      let topPlayerId = null, bottomPlayerId = null;
      if (shouldFlipPlayers) {
        for (const a of prev) {
          if (a.type !== 'stroke') continue;
          if (a.timestamp < gameNRange.startTime || a.timestamp >= gameNRange.endTime) continue;
          if (a.player === 'top'    && topPlayerId    === null) topPlayerId    = a.playerId;
          if (a.player === 'bottom' && bottomPlayerId === null) bottomPlayerId = a.playerId;
          if (topPlayerId !== null && bottomPlayerId !== null) break;
        }
      }

      return pass1.map(a => {
        if (a.type !== 'stroke') return a;
        if (_normScore(a.setScore) !== game.setScore) return a;
        if (a.timestamp >= gameNRange.startTime && a.timestamp < gameNRange.endTime) {
          const pr = gameNPointRanges.find(
            r => a.timestamp >= r.startTime && a.timestamp < r.endTime
          );
          const base = { ...a, gameScore: prevGame.rawGameScore, pointScore: pr?.newPointScore ?? firstMergedScore };
          if (!shouldFlipPlayers) return base;
          const newPlayer   = a.player === 'top' ? 'bottom' : 'top';
          const newPlayerId = a.player === 'top' ? bottomPlayerId : topPlayerId;
          return { ...base, player: newPlayer, playerId: newPlayerId ?? a.playerId };
        }
        return a;
      });
    });
  }, [setAnnotations]);

  const handleSplitGameFromPoint = useCallback((point, game, setGames) => {
    // Find the clicked point and the split boundary (first stroke of the new game).
    const pointIdx = game.points.findIndex(
      p => p.pointScore === point.pointScore && p.startTime === point.startTime
    );
    if (pointIdx < 0) return;
    const firstNewPoint = game.points[pointIdx + 1];
    if (!firstNewPoint) return;
    const splitBoundaryTime = firstNewPoint.startTime;

    // Locate Game N in the set.
    const gameIdx = setGames.findIndex(
      g => g.rawGameScore === game.rawGameScore && g.serverPlayer === game.serverPlayer
    );
    if (gameIdx < 0) return;
    const nextGameInSet = setGames[gameIdx + 1] ?? null;

    // Build time ranges for every game in the set so we can match strokes without
    // relying on gameScore strings (which can collide after normalization).
    const timeRanges = setGames.map((g, i) => ({
      startTime: g.points[0].startTime,
      endTime:   setGames[i + 1]?.points[0].startTime ?? Infinity,
    }));
    const gameNRange = timeRanges[gameIdx];

    // Who won truncated Game N? Infer from the existing score transition.
    const winnerN = nextGameInSet
      ? computeGameWinner(game, nextGameInSet) ?? 'server'
      : 'server';

    // Raw game score for the NEW Game N+1 (split-off portion).
    // Servers alternate, so the new game is served by the other player.
    const newGameN1ServerPlayer = opp(game.serverPlayer);
    const splitScore = computeNewNextGameScore(
      game,
      { serverPlayer: newGameN1ServerPlayer },
      winnerN
    );

    // Who won the split-off Game N+1?  Use the last point score of the split-off
    // portion as a heuristic: whoever is leading at the start of the last rally
    // most likely won.
    const lastSplitPointScore = game.points[game.points.length - 1].pointScore ?? '0-0';
    const inferWinnerFromScore = (ps) => {
      const parseP = (t) => ({ '0': 0, '15': 1, '30': 2, '40': 3, 'A': 4 }[t] ?? (parseInt(t, 10) || 0));
      const [s = '0', r = '0'] = ps.split('-');
      return parseP(s) >= parseP(r) ? 'server' : 'receiver';
    };
    const winnerNewN1 = inferWinnerFromScore(lastSplitPointScore);

    // Cascade: compute new raw game scores for old Game N+1, N+2, …
    // Each entry stores the time range of the affected game and the new score.
    const cascadeUpdates = []; // [ { startTime, endTime, newScore }, … ]
    if (nextGameInSet) {
      const fakeNewGameN1 = { rawGameScore: splitScore, serverPlayer: newGameN1ServerPlayer };
      let prevFake   = fakeNewGameN1;
      let prevWinner = winnerNewN1;

      for (let i = gameIdx + 1; i < setGames.length; i++) {
        const oldGame     = setGames[i];
        const nextOldGame = setGames[i + 1] ?? null;

        const newScore = computeNewNextGameScore(prevFake, oldGame, prevWinner);
        cascadeUpdates.push({
          startTime: timeRanges[i].startTime,
          endTime:   timeRanges[i].endTime,
          newScore,
        });

        const oldWinner = nextOldGame ? computeGameWinner(oldGame, nextOldGame) : null;
        if (!oldWinner) break;

        prevFake   = { rawGameScore: newScore, serverPlayer: oldGame.serverPlayer };
        prevWinner = oldWinner;
      }
    }

    // Compute new point scores for the split-off portion, resetting to 0-0.
    // We determine each point's outcome from consecutive original point scores and
    // re-run the sequence from scratch.
    const splitPoints = game.points.slice(pointIdx + 1);
    const newPointScores = [];
    let runningPS = '0-0';
    for (let i = 0; i < splitPoints.length; i++) {
      newPointScores.push(runningPS);
      const nextSP = splitPoints[i + 1];
      if (!nextSP) break;
      const outcome = getOutcome(splitPoints[i].pointScore, nextSP.pointScore);
      if (!outcome) break;
      runningPS = advanceScoreExtended(runningPS, outcome);
    }
    // Time ranges for each split-off point (for per-stroke point score assignment).
    const splitPointRanges = splitPoints.map((p, i) => ({
      startTime:    p.startTime,
      endTime:      splitPoints[i + 1]?.startTime ?? gameNRange.endTime,
      newPointScore: newPointScores[i] ?? '0-0',
    }));

    // Players switch sides after every odd-numbered game (0-indexed: after game 0, 2, 4, …).
    // A switch occurs between game gi and the new split-off game gi+1 when gi is even.
    // Exception: in a tiebreak (gameScore === '6-6') sides switch every 6 tiebreak points,
    // not at game boundaries. Only flip when the split lands on a 6-point boundary.
    const isTiebreak = game.gameScore === '6-6';
    const shouldFlipPlayers = isTiebreak
      ? (pointIdx + 1) % 6 === 0
      : gameIdx % 2 === 0;

    setAnnotations(prev => {
      // Pass 1: shift old Game N+1, N+2, … to their new scores by time range.
      const pass1 = cascadeUpdates.length > 0
        ? prev.map(a => {
            if (a.type !== 'stroke') return a;
            if (_normScore(a.setScore) !== game.setScore) return a;
            const hit = cascadeUpdates.find(
              u => a.timestamp >= u.startTime && a.timestamp < u.endTime
            );
            return hit ? { ...a, gameScore: hit.newScore } : a;
          })
        : prev;

      // Pass 2: move split-off strokes (in Game N, at/after split boundary) to the
      // new game, reset point scores to 0-0, and flip player/playerId if there is
      // a side switch between Game N and the newly created Game N+1.
      // Precompute top↔bottom playerId mapping from the split-off strokes.
      let topPlayerId = null, bottomPlayerId = null;
      if (shouldFlipPlayers) {
        for (const a of prev) {
          if (a.type !== 'stroke') continue;
          if (a.timestamp < splitBoundaryTime || a.timestamp >= gameNRange.endTime) continue;
          if (a.player === 'top'    && topPlayerId    === null) topPlayerId    = a.playerId;
          if (a.player === 'bottom' && bottomPlayerId === null) bottomPlayerId = a.playerId;
          if (topPlayerId !== null && bottomPlayerId !== null) break;
        }
      }

      return pass1.map(a => {
        if (a.type !== 'stroke') return a;
        if (_normScore(a.setScore) !== game.setScore) return a;
        if (
          a.timestamp >= gameNRange.startTime &&
          a.timestamp <  gameNRange.endTime &&
          a.timestamp >= splitBoundaryTime
        ) {
          const pr = splitPointRanges.find(
            r => a.timestamp >= r.startTime && a.timestamp < r.endTime
          );
          const base = { ...a, gameScore: splitScore, pointScore: pr?.newPointScore ?? '0-0' };
          if (!shouldFlipPlayers) return base;
          const newPlayer   = a.player === 'top' ? 'bottom' : 'top';
          const newPlayerId = a.player === 'top' ? bottomPlayerId : topPlayerId;
          return { ...base, player: newPlayer, playerId: newPlayerId ?? a.playerId };
        }
        return a;
      });
    });
  }, [setAnnotations]);

  const getCurrentAnnotationData = () => {
    if (!playerRef.current) return null;

    const currentFPS = getFPS();
    if (!currentFPS) {
      console.error('Cannot generate annotations: FPS not set');
      return null;
    }

    const actualVideoWidth = playerRef.current?.videoWidth || 1280;
    const actualVideoHeight = playerRef.current?.videoHeight || 720;

    // Use loadedFileName state if available, otherwise try to extract from URL
    let videoFilename = '';
    if (loadedFileName) {
      // Remove the file extension for cleaner filenames
      videoFilename = loadedFileName.replace(/\.[^/.]+$/, '');
    } else {
      // Fallback: try to extract from URL
      try {
        if (videoUrl) {
          const urlWithoutParams = videoUrl.split('?')[0];
          const urlParts = urlWithoutParams.split('/');
          const lastPart = urlParts[urlParts.length - 1];
          if (lastPart.toLowerCase().endsWith('.mp4')) {
            // Remove the .mp4 extension for cleaner filenames
            videoFilename = lastPart.slice(0, -4);
          }
        }
      } catch (error) {
        console.warn('Could not extract video filename from URL:', error);
      }
    }

    // Format ball annotations — convert exact PTS back to frame/fps for file compatibility
    const currentFPSForSave2 = getFPS();
    const ballAnnotationsData = annotationsToSave.map(annotation => {
      const frame = getFrameForTime(annotation.timestamp);
      const fileTime = currentFPSForSave2 ? frame / currentFPSForSave2 : annotation.timestamp;
      const baseData = {
        time: fileTime,
        timestamp: formatTime(fileTime),
        x: Math.round(annotation.x),
        y: Math.round(annotation.y),
        type: annotation.type
      };

      // Add meter coordinates if they exist (from camera calibration)
      if (annotation.x_m !== undefined && annotation.y_m !== undefined) {
        baseData.x_m = annotation.x_m;
        baseData.y_m = annotation.y_m;
        if (annotation.z_m !== undefined) {
          baseData.z_m = annotation.z_m;
        }
      }

      // Add bounding box data if it exists
      if (annotation.boundingBox) {
        baseData.boundingBox = {
          x1: Math.round(annotation.boundingBox.x1),
          y1: Math.round(annotation.boundingBox.y1),
          x2: Math.round(annotation.boundingBox.x2),
          y2: Math.round(annotation.boundingBox.y2)
        };
      }

      if (annotation.flaggedForReview) {
        baseData.flaggedForReview = true;
      }
      if (annotation.customLabel && annotation.customLabel !== 'none') {
        baseData.customLabel = annotation.customLabel;
      }

      // Only add player, strokeType and scoring fields for stroke annotations
      if (annotation.type === 'stroke') {
        const strokeData = {
          ...baseData,
          player:     annotation.player     || 'bottom',
          strokeType: annotation.strokeType || 'forehand',
        };
        if (annotation.playerId)   strokeData.playerId   = annotation.playerId;
        if (annotation.pointScore) strokeData.pointScore = annotation.pointScore;
        if (annotation.gameScore)  strokeData.gameScore  = annotation.gameScore;
        if (annotation.setScore)   strokeData.setScore   = annotation.setScore;
        if (annotation.rallyId != null) strokeData.rallyId = annotation.rallyId;
        return strokeData;
      }

      return baseData;
    });

    // Format player annotations
    const playerAnnotationsData = playerAnnotations.map(annotation => {
      const frame = getFrameForTime(annotation.timestamp);
      const fileTime = currentFPSForSave2 ? frame / currentFPSForSave2 : annotation.timestamp;
      const baseData = {
        time: fileTime,
        timestamp: formatTime(fileTime),
        player: annotation.player,
        boundingBox: {
          x1: Math.round(annotation.boundingBox.x1),
          y1: Math.round(annotation.boundingBox.y1),
          x2: Math.round(annotation.boundingBox.x2),
          y2: Math.round(annotation.boundingBox.y2)
        },
        strokeScore: annotation.strokeScore || 0,
        strokeType: annotation.strokeType || null
      };

      // Add meter coordinates if they exist (from camera calibration)
      if (annotation.x_m !== undefined && annotation.y_m !== undefined) {
        baseData.x_m = annotation.x_m;
        baseData.y_m = annotation.y_m;
      }

      return baseData;
    });

    // Format court keypoints (filter out reprojected keypoints)
    const courtKeypointsData = allCourtKeypoints
      .filter(court => !court.reprojected)
      .map(court => {
        const frame = getFrameForTime(court.timestamp);
        const fileTime = currentFPSForSave2 ? frame / currentFPSForSave2 : court.timestamp;
        return {
          time: fileTime,
          timestamp: formatTime(fileTime),
        keypoints: court.keypoints
        };
      });

    return {
      video_filename: videoFilename,
      video_width: actualVideoWidth,
      video_height: actualVideoHeight,
      fps: currentFPS,
      total_frames: videoDuration ? Math.floor(videoDuration * currentFPS) : 0,
      total_duration: videoDuration ? formatTime(videoDuration) : '00:00.00',
      ball_annotations: ballAnnotationsData,
      player_annotations: playerAnnotationsData,
      court_keypoints: courtKeypointsData,
      ...(trajectories.length > 0 && { trajectories }),
    };
  };

  // Helper function to format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 100);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}.${ms.toString().padStart(2, '0')}`;
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Handle send video for processing
  const handleSendVideoForProcessing = async (mode = 'singles') => {
    // Reset upload state
    setUploadError('');
    setUploadSuccess(false);
    setUploadProgress(0);
    
    try {
      // 1. Record usage FIRST — blocks upload if limit reached or API unreachable
      const trackingResult = await usageTrackingService.trackVideoSentForProcessing(loadedFileName);
      
      if (!trackingResult.success) {
        console.error('Video processing recording failed:', trackingResult.error);
        setUploadError(trackingResult.error || 'Cannot process video — please try again');
        return;
      }

      console.log('Video processing recorded, remaining:', trackingResult.usage?.remaining);
      
      // 2. Upload the video
      setIsUploadingVideo(true);
      
      if (localVideoFile) {
        const uploadResult = await videoUploadService.uploadVideo(
          localVideoFile,
          (progress) => setUploadProgress(progress),
          videoDuration,
          mode
        );
        console.log('Video uploaded successfully:', uploadResult);
        
      } else if (processedVideoUrl && !processedVideoUrl.startsWith('blob:')) {
        try {
          const uploadResult = await videoUploadService.uploadVideoFromUrl(
            processedVideoUrl,
            loadedFileName || 'video.mp4',
            (progress) => setUploadProgress(progress),
            videoDuration,
            mode
          );
          console.log('Video uploaded from URL successfully:', uploadResult);
        } catch (urlError) {
          setUploadError('Cannot upload remote video. Please download and upload the video file directly.');
          console.error('Failed to upload from URL (likely CORS):', urlError);
          return;
        }
      } else {
        setUploadError('No video file available for upload');
        return;
      }
      
      // 3. Success
        setUploadSuccess(true);

      // Reload subscription from Firestore to update the video processing counter in UI
      // (no Paddle verification — avoids overwriting status for trial/test accounts)
        try {
        await reloadSubscription();
      } catch (reloadErr) {
        console.warn('Failed to reload subscription after upload:', reloadErr);
        }

        // Auto-close after 2 seconds
        setTimeout(() => {
          setIsSendVideoPopupOpen(false);
          setUploadSuccess(false);
        }, 2000);
      
    } catch (error) {
      console.error('Error sending video for processing:', error);
      setUploadError(error.message || 'Upload failed');
    } finally {
      setIsUploadingVideo(false);
    }
  };

  return (
    <VideoPlayerContainer tabIndex={0}>
      <Sidebar
        videoUrl={videoUrl}
        onVideoUrlChange={handleVideoUrlChange}
        onVideoUrlSubmit={handleVideoUrlSubmit}
        onVideoFileUpload={handleVideoFileUpload}
        onLoadVideo={loadVideoWithMediaInfo}
        videoError={videoError}
        annotationMode={annotationMode}
        annotationCounts={annotationCounts}
        annotationProgress={annotationProgress}
        navigateToAnnotation={navigateToAnnotation}
        loadedFileName={annotationFileName}
        uploadError={uploadError}
        onAnnotationUpload={handleAnnotationUpload}
        onClearAnnotations={handleClearAnnotations}
        currentTime={currentTime}
        annotations={annotations}
        playerAnnotations={playerAnnotations}
        courtKeypoints={allCourtKeypoints}
        isCourtMode={isCourtMode}
        isPlayerMode={isPlayerMode}
        isRallyMode={isRallyMode}
        rallyScoring={{
          nearPlayer,
          farPlayer,
          onNearPlayerChange: setNearPlayer,
          onFarPlayerChange: setFarPlayer,
          initialServer,
          onInitialServerChange: setInitialServer,
          adScoring,
          onAdScoringChange: setAdScoring,
          rallies,
          scoreboard,
          pendingStart,
          awaitingWinnerId,
          incompleteRallies,
          markError: rallyMarkError,
          loadedFileName: rallyFileName,
          onStartRally: handleStartRally,
          onEndRally: handleEndRally,
          onCancelPendingRally: cancelPendingRally,
          onSetWinner: setRallyWinner,
          onSetRallyTime: handleSetRallyTime,
          onDeleteRally: deleteRally,
          onSave: handleSaveRallies,
          onLoad: handleLoadRallies,
        }}
        navigateToCourtKeypoint={navigateToCourtKeypoint}
        playerFilter={playerFilter}
        onPlayerFilterChange={setPlayerFilter}
        strokeTypeFilter={strokeTypeFilter}
        onStrokeTypeFilterChange={setStrokeTypeFilter}
        volleyFilter={volleyFilter}
        onVolleyFilterChange={setVolleyFilter}
        volleyProgress={volleyProgress}
        getCurrentAnnotationData={getCurrentAnnotationData}
        saveCourtCoordinates={saveCourtCoordinates}
        onSaveCourtCoordinatesChange={handleSaveCourtCoordinatesChange}
        showTrajectories={showTrajectories}
        showCall={showCall}
        onShowCallChange={setShowCall}
        onShowTrajectoriesChange={setShowTrajectories}
        onRecalibrateCamera={recalibrateCameraParameters}
        isProcessingCamera={isProcessingCamera}
        onStartTutorial={startTutorial}
        onTutorialActionComplete={handleActionComplete}
        onLogout={handleLogout}
        onAzurePopupOpen={() => setIsAzurePopupOpen(true)}
        onBillingClick={() => setIsBillingOpen(true)}
        onSendVideoForProcessing={() => setIsSendVideoPopupOpen(true)}
        hasVideoLoaded={!!processedVideoUrl}
        videoProcessingRemaining={subscription?.videoProcessingRemaining}
        reviewCounts={reviewCounts}
        navigateToReview={navigateToReview}
        rallyGroups={rallyGroups}
        navigateToRally={navigateToRally}
        navigateToCustomLabel={navigateToCustomLabel}
        customLabels={customLabels}
        onAddCustomLabel={addCustomLabel}
        onRemoveCustomLabel={removeCustomLabel}
        currentAnnotationLabel={currentAnnotationLabel}
        activeRallyStartTime={activeRallyStartTime}
        onSeek={handleScoringSeek}
        onMergeWithPrevious={handleMergePointWithPrevious}
        onAddPointAfter={handleAddPointAfter}
        onTogglePointWinner={handleTogglePointWinner}
        onToggleGameWinner={handleToggleGameWinner}
        onToggleSetWinner={handleToggleSetWinner}
        onSplitGame={handleSplitGameFromPoint}
        onMergeGameWithPrevious={handleMergeGameWithPrevious}
      />

      <VideoArea>
        <VideoWrapper
          ref={videoWrapperRef}
          isPaused={isPaused}
          onWheel={handleWheel}
          onMouseMove={handleVideoWrapperMouseMove}
          style={{
            width: `${displayDimensions.width}px`,
            height: `${displayDimensions.height}px`
          }}
        >
          {processedVideoUrl ? (
            <>
              <VideoContent
                pan={pan}
                zoom={zoom}
                style={{
                  width: `${displayDimensions.width}px`,
                  height: `${displayDimensions.height}px`
                }}
              >
                <VideoCanvas
                  ref={canvasRef}
                  data-tutorial="video-player"
                />
              </VideoContent>
              {/* Top View Plot Overlay - Temporarily disabled (corner overlay not precise enough yet)
                  To re-enable: change `false &&` to just render the component
              */}
              {false && (
                <TopViewPlotOverlay
                  playerAnnotations={playerAnnotations}
                  currentTime={currentTime}
                />
              )}
              {isBallMode && showTrajectories && trajectories.length > 0 && (
                <TrajectoryOverlay
                  trajectories={trajectories}
                  currentTime={overlayTime}
                  zoom={zoom}
                  pan={pan}
                  videoRef={canvasRef}
                  displayDimensions={displayDimensions}
                />
              )}
              {(isBallMode || isPlayerMode) && (
                <AnnotationLayer
                  annotations={isBallMode ? currentAnnotations : []}
                  annotationsByFrame={isBallMode ? annotationsByFrame : null}
                  playerAnnotations={getCurrentPlayerAnnotations()}
                  playerAnnotationsByFrame={playerAnnotationsByFrame}
                  playerIdMap={playerIdMap}
                  videoRef={canvasRef}
                  zoom={zoom}
                  pan={pan}
                  currentTime={overlayTime}
                  isPlayerMode={isPlayerMode}
                  isPaused={isPaused}
                  onPlayerAnnotationChange={addPlayerAnnotation}
                  displayDimensions={displayDimensions}
                />
              )}
              {isBallMode && (
                <AnnotationLayer
                  annotations={[]}
                  playerAnnotations={[]}
                  videoRef={canvasRef}
                  zoom={zoom}
                  pan={pan}
                  onMouseDown={handleAnnotationMouseDown}
                  onContextMenu={handleContextMenu}
                  isClickable={isPaused}
                  displayDimensions={displayDimensions}
                />
              )}
              {isBallMode && showCall && currentStrokeIn !== null && (
                <CallCard>
                  <InOutBadge $in={currentStrokeIn}>
                    {currentStrokeIn ? 'IN' : 'OUT'}
                  </InOutBadge>
                  {currentStrokeInfo?.lineConfidence != null && (
                    <CallConfidence>
                      {Math.round(currentStrokeInfo.lineConfidence * 100)}%
                    </CallConfidence>
                  )}
                </CallCard>
              )}
              {isCourtMode && currentCourtKeypoints && (
                <CourtOverlay
                  keypoints={Array.isArray(currentCourtKeypoints) ? currentCourtKeypoints : currentCourtKeypoints.keypoints}
                  onKeypointsChange={handleCourtKeypointsChange}
                  onSave={handleCourtKeypointsSave}
                  onPredict={handleCourtPrediction}
                  videoRef={canvasRef}
                  zoom={zoom}
                  pan={pan}
                  displayDimensions={displayDimensions}
                />
              )}
            </>
          ) : videoError && videoError.includes('Analyzing video metadata') ? (
            <EmptyState>
              <EmptyStateIcon>
                <EmptyStateLogo src="/images/logo_purple.svg" alt="Tater Logo" />
              </EmptyStateIcon>
              <div style={{
                color: '#50fa7b',
                fontWeight: '500'
              }}>
                {videoError}
              </div>
            </EmptyState>
          ) : (
            <EmptyState>
              <EmptyStateIcon>
                <EmptyStateLogo src="/images/logo_purple.svg" alt="Tater Logo" />
              </EmptyStateIcon>
              Please enter a video URL or upload a video
            </EmptyState>
          )}
        </VideoWrapper>

        <Timeline
          currentTime={currentTime}
          videoDuration={videoDuration}
          annotations={annotations}
          playerRef={playerRef}
          isPaused={isPaused}
          isBallMode={isBallMode}
          isRallyMode={isRallyMode}
          rallies={rallies}
          scoreboard={scoreboard}
          pendingStart={pendingStart}
          timelineZoom={timelineZoom}
          setTimelineZoom={setTimelineZoom}
          onPlayPause={handlePlayPauseClick}
        />

        <VideoControls
          isPaused={isPaused}
          onPlayPause={handlePlayPauseClick}
          currentTime={currentTime}
          videoDuration={videoDuration}
          annotations={annotations}
          courtKeypoints={allCourtKeypoints}
          playerRef={playerRef}
          videoMode={videoMode}
          isCourtMode={isCourtMode}
          isPlayerMode={isPlayerMode}
          onToggleMode={handleModeToggle}
          onModeSelect={setVideoModeDirect}
          timelineZoom={timelineZoom}
          onTimelineZoom={setTimelineZoom}
          playbackRate={playbackRate}
          onPlaybackRateChange={setPlaybackRate}
        />

      </VideoArea>

      <LogoContainer>
        <Logo src="/images/logo%20SplitStep,%20symbol%2003.png" alt="SplitStep Logo" />
        <Logo src="/images/logo%20Wyatt%20AI%20wide,%20dark.png" alt="Wyatt AI Logo" />
        <FeedbackButton onClick={() => setIsFeedbackOpen(true)}>
          Feedback
        </FeedbackButton>
      </LogoContainer>

      <ShortcutsOverlay
        isVisible={showShortcuts}
        onClose={() => setShowShortcuts(false)}
        onOpenInNewTab={openShortcutsInNewTab}
      />
      <FeedbackPopup
        isOpen={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
      />
      <TutorialOverlay
        isVisible={isTutorialActive}
        onClose={stopTutorial}
        currentStep={currentStepIndex}
        totalSteps={totalSteps}
        step={currentStep}
        onNext={nextStep}
        onPrevious={previousStep}
        onSkip={skipTutorial}
        highlightElement={currentStep?.highlightElement}
        tooltipPosition={currentStep?.tooltipPosition}
      />
      <AzureStoragePopup
        isOpen={isAzurePopupOpen}
        onClose={() => setIsAzurePopupOpen(false)}
        isEmployee={isEmployee}
        onLoadAnnotation={async (annotationData, fileName) => {
          // Pass pre-parsed data directly to loadAnnotations (no re-serializing!)
          try {
            let success = false;
            const data = annotationData;

            // Check if this is the new stroke format
            const isNewStrokeFormat = Array.isArray(data) &&
              data.length > 0 &&
              data[0].player_x1_px !== undefined &&
              data[0].opponent_x1_px !== undefined;

            if (isNewStrokeFormat) {
              const result = await loadAnnotations(data);
              if (result && result.success) success = true;
            } else {
              // Load ball annotations
              if (data.ball_annotations && Array.isArray(data.ball_annotations)) {
                const ballResult = await loadAnnotations({ ...data, annotations: data.ball_annotations });
                if (ballResult && ballResult.success) success = true;
              }
              // Load court keypoints
              if (data.court_keypoints && Array.isArray(data.court_keypoints)) {
                const courtBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
                const courtFile = new File([courtBlob], fileName, { type: 'application/json' });
                const courtResult = await loadCourtKeypoints(courtFile);
                if (courtResult && courtResult.success) success = true;
              }
              // Load camera parameters
              if (data.camera_parameters) {
                setStoredCameraParams(data.camera_parameters);
                processingInProgressRef.current = false;
                success = true;
              }
              // Try old format
              if (!data.ball_annotations && !data.court_keypoints && data.annotations) {
                const result = await loadAnnotations(data);
                if (result && result.success) success = true;
              }
            }

            if (success) {
              setLoadedFileName(fileName);
              setAnnotationFileName(fileName);
              setUploadError('');
            } else {
              setUploadError('Invalid annotation file format.');
              setLoadedFileName('');
              setAnnotationFileName('');
            }
          } catch (error) {
            setUploadError('Error loading annotation file.');
            setLoadedFileName('');
            setAnnotationFileName('');
          }
        }}
        onGetCurrentAnnotations={getCurrentAnnotationData}
        onGetCurrentStrokes={hasStrokeRecords ? buildStrokeRecordsForSave : undefined}
        strokesFileName={annotationFileName}
        onLoadVideo={loadVideoWithMediaInfo}
      />
      <BillingModal
        isOpen={isBillingOpen}
        onClose={() => setIsBillingOpen(false)}
        onShowPlans={() => {
          setIsBillingOpen(false);
          setIsPlansOpen(true);
        }}
      />
      <PlansModal
        isOpen={isPlansOpen}
        onClose={() => setIsPlansOpen(false)}
        onPlanSelected={() => setIsPlansOpen(false)}
      />
      <SendVideoPopup
        isOpen={isSendVideoPopupOpen}
        onClose={() => {
          if (!isUploadingVideo) {
            setIsSendVideoPopupOpen(false);
            setUploadError('');
            setUploadSuccess(false);
            setUploadProgress(0);
          }
        }}
        onConfirm={handleSendVideoForProcessing}
        videoName={loadedFileName}
        isUploading={isUploadingVideo}
        uploadProgress={uploadProgress}
        uploadError={uploadError}
        uploadSuccess={uploadSuccess}
      />
    </VideoPlayerContainer>
  );
};

export default VideoPlayer; 